import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Share,
  StyleSheet,
  SafeAreaView,
  Modal,
  TextInput,
  Alert,
  Image,
  Platform,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
// Deploy trigger update
import { router, useLocalSearchParams } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import QRCode from 'react-native-qrcode-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useRacha } from '@/contexts/RachaContext';
import { useGrupos } from '@/contexts/GruposContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ArrowLeft, Plus, Calendar, User, Users, QrCode, Share as ShareIcon, UserPlus, SquareCheck as CheckSquare, RotateCcw, Trash2, Edit3, X, Info, Lock, Check } from 'lucide-react-native';
import { PremiumFeatureModal } from '@/components/PremiumFeatureModal';
import { formatarData } from '@/utils/formatarData';
import ChavePixSelector from '@/components/ChavePixSelector';
import Breadcrumb from '@/components/Breadcrumb';
import DivisaoDespesa from '@/components/DivisaoDespesa';
import CategoriaSelector from '@/components/CategoriaSelector';
import { MoneyInput } from '@/components/MoneyInput';
import { categoriasPadrao } from '@/constants/categorias';
import { ModoDivisao, DivisaoPersonalizada, Categoria, GrupoDeBolso } from '@/types/app';
import ViewShot from 'react-native-view-shot';
import * as Sharing from 'expo-sharing';
import { testBalanceCalculator, testEqualDivision, testManyParticipants, testDataTypeIssues } from '@/utils/testBalanceCalculator';
import { calculateBalance } from '@/utils/balanceCalculator';
import PremiumCTA from '@/components/PremiumCTA';
import BannerIntersticial from '@/components/BannerIntersticial';
import AdSenseBanner from '@/components/AdSenseBanner';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '@/utils/logger';

// Substituir função renderBalanco por componente React
interface Transacao {
  devedorNome: string;
  credorNome: string;
  valor: number;
  credorId: string;
  devedorId: string;
}
interface Participante {
  id: string;
  nome: string;
  foto: string;
  chavePix?: { valor: string };
  grupoDeBolsoId?: string | null;
}
interface BalancoProps {
  transacoes: Transacao[];
  racha: any;
  participantes: Participante[];
  formatarValor: (valor: number) => string;
  copiarChavePix: (chave: string) => void;
  colors: any;
  isPremium: () => boolean;
}
function Balanco({ transacoes, racha, participantes, formatarValor, copiarChavePix, colors, isPremium }: BalancoProps) {
  const [isSharing, setIsSharing] = React.useState(false);
  const [showSharePreview, setShowSharePreview] = React.useState(false);
  const viewShotRef = React.useRef<any>(null);
  const { width } = useWindowDimensions();
  const isNarrowScreen = width < 400;



  // Função para gerar texto de compartilhamento com credores
  const gerarTextoCompartilhamento = async () => {

    // Calcular total das despesas
    const totalDespesas = racha.despesas.reduce((total: number, despesa: any) => total + despesa.valor, 0);

    // Iniciar texto com informações do racha
    let texto = `${racha.emoji} ${racha.nome}\n`;
    texto += `${formatarData(racha.dataCriacao)}\n`;
    texto += `Total: ${formatarValor(totalDespesas)}\n\n`;

    // Seção de despesas
    if (racha.despesas && racha.despesas.length > 0) {
      texto += '📋 DESPESAS:\n';
      racha.despesas.forEach((despesa: any) => {
        texto += `${despesa.categoria?.emoji || '💰'} ${despesa.titulo}\n`;
        texto += `   Pago por: ${despesa.pagoPorNome} - ${formatarValor(despesa.valor)}\n\n`;
      });
    }

    // Seção de quem deve para quem
    if (transacoes && transacoes.length > 0) {
      texto += '💰 QUEM DEVE PARA QUEM:\n';
      transacoes.forEach((transacao: Transacao) => {
        texto += `${transacao.devedorNome} deve ${formatarValor(transacao.valor)} para ${transacao.credorNome}\n`;
      });
      texto += '\n';
    }

    // Identificar credores únicos (participantes com saldo positivo)
    const credoresMap = new Map();

    // Importar funções do Firestore
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('@/utils/firebaseConfig');

    transacoes.forEach((transacao: Transacao) => {
      const credorId = transacao.credorId;
      const credorNome = transacao.credorNome;

      // Verificar se o credor é um grupo de bolso
      const grupoDebolso = racha?.gruposDeBolso?.find((g: any) => g.id === credorId);

      if (!credoresMap.has(credorId)) {
        if (grupoDebolso) {
          // É um grupo de bolso - coletar membros com suas chaves Pix
          const membrosComPix: Array<{ nome: string, chavePix: string, participanteId: string }> = [];

          racha.participantes.forEach((p: any) => {
            if (p.grupoDeBolsoId === credorId) {
              membrosComPix.push({
                nome: p.nome,
                chavePix: p.chavePix?.valor || '',
                participanteId: p.id
              });
            }
          });

          credoresMap.set(credorId, {
            nome: credorNome,
            membrosComPix: membrosComPix,
            isGrupo: true
          });
        } else {
          // É um participante individual
          const credor = participantes.find((p: Participante) => p.id === credorId);
          credoresMap.set(credorId, {
            nome: credorNome,
            chavesPix: credor?.chavePix?.valor ? [credor.chavePix.valor] : [],
            isGrupo: false,
            credorId: credorId // Adicionar ID para buscar no Firestore
          });
        }
      }
    });

    if (credoresMap.size === 0) {
      return texto + 'Não há pendências financeiras neste racha! 🎉';
    }

    // Buscar chaves PIX dos usuários no Firestore
    for (const [credorId, credorInfo] of credoresMap.entries()) {
      if (!credorInfo.isGrupo && credorInfo.chavesPix.length === 0) {
        try {
          // Buscar documento do usuário na coleção 'usuarios'
          const userDoc = await getDoc(doc(db, 'usuarios', credorId));
          if (userDoc.exists() && userDoc.data().chavePix?.valor) {
            credorInfo.chavesPix = [userDoc.data().chavePix.valor];
          }
        } catch (error) {
          console.error(`Erro ao buscar chave PIX para ${credorInfo.nome}:`, error);
        }
      } else if (credorInfo.isGrupo) {
        // Para vínculos de bolso, buscar chaves PIX de cada membro
        for (const membro of credorInfo.membrosComPix) {
          if (!membro.chavePix) {
            try {
              // Buscar documento do usuário na coleção 'usuarios'
              const userDoc = await getDoc(doc(db, 'usuarios', membro.participanteId));
              if (userDoc.exists() && userDoc.data().chavePix?.valor) {
                membro.chavePix = userDoc.data().chavePix.valor;
              }
            } catch (error) {
              console.error(`Erro ao buscar chave PIX para ${membro.nome}:`, error);
            }
          }
        }
      }
    }

    texto += '📱 CHAVES PIX PARA PAGAMENTO:\n\n';

    credoresMap.forEach((credorInfo, credorId) => {
      if (credorInfo.isGrupo && credorInfo.membrosComPix && credorInfo.membrosComPix.length > 0) {
        // Para vínculos de bolso, primeiro mostrar o nome do grupo
        texto += `- ${credorInfo.nome}\n`;

        // Depois listar cada membro com recuo (apenas os que têm chave Pix)
        const membrosComPix = credorInfo.membrosComPix.filter((membro: { nome: string, chavePix: string, participanteId: string }) => membro.chavePix);

        if (membrosComPix.length > 0) {
          membrosComPix.forEach((membro: { nome: string, chavePix: string, participanteId: string }) => {
            texto += `  ${membro.nome}\n`;
            texto += `  PIX: ${membro.chavePix}\n\n`;
          });
        } else {
          texto += '\n';
        }
      } else {
        // Para participantes individuais
        texto += `- ${credorInfo.nome}\n`;
        if (credorInfo.chavesPix && credorInfo.chavesPix.length > 0) {
          if (credorInfo.chavesPix.length === 1) {
            texto += `  PIX: ${credorInfo.chavesPix[0]}\n`;
          } else {
            texto += `  PIX: ${credorInfo.chavesPix.join(' ou ')}\n`;
          }
        }
        texto += '\n';
      }
    });

    return texto;
  };

  // Função para gerar imagem usando canvas (web)
  const generateBalanceImage = async (texto: string, racha: any, colors: any): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Não foi possível criar contexto do canvas'));
        return;
      }

      // Configurações do canvas - aumentar altura para incluir despesas e transações
      canvas.width = 600;
      canvas.height = 1600;

      // Calcular total das despesas
      const totalDespesas = racha.despesas.reduce((total: number, despesa: any) => total + despesa.valor, 0);

      // Fundo
      ctx.fillStyle = colors.background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Configurações de texto - AUMENTAR FONTES
      ctx.fillStyle = colors.text;
      ctx.font = 'bold 40px Arial';
      ctx.textAlign = 'center';

      // Título
      ctx.fillText(`${racha.emoji} ${racha.nome}`, canvas.width / 2, 70);

      // Data
      ctx.font = '26px Arial';
      ctx.fillStyle = colors.textSecondary;
      ctx.fillText(formatarData(racha.dataCriacao), canvas.width / 2, 110);

      // Total
      ctx.font = 'bold 34px Arial';
      ctx.fillStyle = colors.text;
      ctx.fillText(`Total: ${formatarValor(totalDespesas)}`, canvas.width / 2, 160);

      // Seção de Despesas
      let currentY = 200;

      if (racha.despesas && racha.despesas.length > 0) {
        // Título da seção de despesas
        ctx.font = 'bold 30px Arial';
        ctx.fillStyle = colors.primary;
        ctx.textAlign = 'center';
        ctx.fillText('DESPESAS', canvas.width / 2, currentY);
        currentY += 40;

        // Linha separadora
        ctx.strokeStyle = colors.border;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(50, currentY);
        ctx.lineTo(canvas.width - 50, currentY);
        ctx.stroke();
        currentY += 25;

        // Listar despesas
        ctx.textAlign = 'left';
        racha.despesas.forEach((despesa: any, index: number) => {
          if (currentY > canvas.height - 200) return; // Evitar overflow

          // Nome da despesa
          ctx.font = 'bold 26px Arial';
          ctx.fillStyle = colors.text;
          ctx.fillText(`${despesa.categoria?.emoji || '💰'} ${despesa.titulo}`, 50, currentY);
          currentY += 25;

          // Quem pagou e valor
          ctx.font = '22px Arial';
          ctx.fillStyle = colors.textSecondary;
          ctx.fillText(`Pago por: ${despesa.pagoPorNome}`, 70, currentY);

          // Valor alinhado à direita
          ctx.textAlign = 'right';
          ctx.fillStyle = colors.primary;
          ctx.fillText(formatarValor(despesa.valor), canvas.width - 50, currentY);
          ctx.textAlign = 'left';

          currentY += 30;

          // Linha separadora entre despesas (exceto a última)
          if (index < racha.despesas.length - 1) {
            ctx.strokeStyle = colors.border + '50';
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(70, currentY);
            ctx.lineTo(canvas.width - 70, currentY);
            ctx.stroke();
            currentY += 20;
          }
        });

        currentY += 25;
      }

      // Seção de quem deve para quem
      if (transacoes && transacoes.length > 0) {
        // Linha separadora
        ctx.strokeStyle = colors.border;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(50, currentY);
        ctx.lineTo(canvas.width - 50, currentY);
        ctx.stroke();
        currentY += 40;

        // Título da seção
        ctx.font = 'bold 30px Arial';
        ctx.fillStyle = colors.primary;
        ctx.textAlign = 'center';
        ctx.fillText('💰 QUEM DEVE PARA QUEM', canvas.width / 2, currentY);
        currentY += 40;

        // Listar transações
        ctx.textAlign = 'left';
        ctx.font = '24px Arial';
        ctx.fillStyle = colors.text;

        transacoes.forEach((transacao: Transacao) => {
          if (currentY > canvas.height - 200) return; // Evitar overflow

          const textoTransacao = `${transacao.devedorNome} deve ${formatarValor(transacao.valor)} para ${transacao.credorNome}`;
          ctx.fillText(textoTransacao, 50, currentY);
          currentY += 35;
        });

        currentY += 25;
      }

      // Linha separadora principal
      ctx.strokeStyle = colors.border;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(50, currentY);
      ctx.lineTo(canvas.width - 50, currentY);
      ctx.stroke();
      currentY += 40;

      // Título da seção de chaves PIX
      ctx.font = 'bold 30px Arial';
      ctx.fillStyle = colors.primary;
      ctx.textAlign = 'center';
      ctx.fillText('📱 CHAVES PIX', canvas.width / 2, currentY);
      currentY += 40;

      // Texto das chaves PIX
      ctx.font = '24px Arial';
      ctx.fillStyle = colors.text;
      ctx.textAlign = 'left';

      const lines = texto.split('\n');
      let inPixSection = false;

      lines.forEach((line, index) => {
        if (currentY > canvas.height - 50) return; // Evitar overflow

        // Detectar início da seção de chaves PIX
        if (line.includes('📱 CHAVES PIX PARA PAGAMENTO:')) {
          inPixSection = true;
          return; // Pular esta linha pois já temos o título
        }

        // Só renderizar linhas da seção de PIX
        if (!inPixSection) return;

        if (line.trim().startsWith('-')) {
          // Nome do credor
          ctx.font = 'bold 28px Arial';
          ctx.fillStyle = colors.primary;
        } else if (line.trim().startsWith('PIX:')) {
          // Chave PIX
          ctx.font = '22px monospace';
          ctx.fillStyle = colors.textSecondary;
        } else if (line.trim() !== '') {
          // Texto normal (não vazio)
          ctx.font = '24px Arial';
          ctx.fillStyle = colors.text;
        } else {
          // Linha vazia - pular
          currentY += 15;
          return;
        }

        ctx.fillText(line, 50, currentY);
        currentY += 30;
      });

      // Converter canvas para blob
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Falha ao gerar imagem'));
        }
      }, 'image/png');
    });
  };

  // Função para abrir o modal de preview
  const compartilhar = () => {
    setShowSharePreview(true);
  };

  const gerarTextoPixParaPagamento = async () => {
    const getChavePixValor = (p: Participante) => {
      const chave: unknown = (p as any)?.chavePix;
      if (typeof chave === 'string') return chave.trim();
      if (chave && typeof chave === 'object' && typeof (chave as any).valor === 'string') {
        return (chave as any).valor.trim();
      }
      return undefined;
    };

    const getLinkParaChavePix = (valor: string) => {
      const chave = valor.trim();
      if (!chave) return undefined;

      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(chave);
      if (isEmail) return `mailto:${chave}`;

      const digits = chave.replace(/\D/g, '');
      const pareceTelefone =
        (/[+()]/.test(chave) || (/[\s-]/.test(chave) && !chave.includes('.'))) &&
        digits.length >= 10 &&
        digits.length <= 13;
      if (pareceTelefone) {
        const telDigits = digits.startsWith('55') ? digits : `55${digits}`;
        return `tel:+${telDigits}`;
      }

      return `https://www.poenoracha.com.br/pix?chave=${encodeURIComponent(chave)}`;
    };

    if (!transacoes || transacoes.length === 0) {
      return '📱 CHAVES PIX PARA PAGAMENTO:\n\nNenhum pagamento pendente.\n\n--\n';
    }

    const participantesIndex = new Map<string, Participante>();
    participantes.forEach((p: Participante) => participantesIndex.set(p.id, p));
    (racha.participantes || []).forEach((p: Participante) => {
      if (!participantesIndex.has(p.id)) participantesIndex.set(p.id, p);
    });

    const credoresMap = new Map<
      string,
      | { isGrupo: true; nome: string; membros: Array<{ id: string; nome: string; chavePix?: string }> }
      | { isGrupo: false; nome: string; credorId: string; chavePix?: string }
    >();

    transacoes.forEach((t: Transacao) => {
      const credorId = t.credorId;
      if (credoresMap.has(credorId)) return;

      const grupoDeBolso = racha?.gruposDeBolso?.find((g: any) => g.id === credorId);
      if (grupoDeBolso) {
        const membros: Array<{ id: string; nome: string; chavePix?: string }> = [];
        (racha.participantes || []).forEach((p: Participante) => {
          if ((p as any).grupoDeBolsoId === credorId) {
            const valorLocal = getChavePixValor(p);
            membros.push({ id: p.id, nome: p.nome, chavePix: valorLocal || undefined });
          }
        });
        credoresMap.set(credorId, { isGrupo: true, nome: t.credorNome, membros });
        return;
      }

      const pLocal = participantesIndex.get(credorId);
      const valorLocal = pLocal ? getChavePixValor(pLocal) : undefined;
      credoresMap.set(credorId, { isGrupo: false, nome: t.credorNome, credorId, chavePix: valorLocal || undefined });
    });

    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('@/utils/firebaseConfig');

    for (const [, info] of credoresMap.entries()) {
      if (info.isGrupo) {
        for (const membro of info.membros) {
          if (membro.chavePix) continue;
          try {
            const userDoc = await getDoc(doc(db, 'usuarios', membro.id));
            const valor = userDoc.exists() ? userDoc.data()?.chavePix?.valor : undefined;
            if (typeof valor === 'string' && valor.trim()) {
              membro.chavePix = valor.trim();
            }
          } catch {}
        }
      } else {
        if (info.chavePix) continue;
        try {
          const userDoc = await getDoc(doc(db, 'usuarios', info.credorId));
          const valor = userDoc.exists() ? userDoc.data()?.chavePix?.valor : undefined;
          if (typeof valor === 'string' && valor.trim()) {
            info.chavePix = valor.trim();
          }
        } catch {}
      }
    }

    let textoPix = '📱 CHAVES PIX PARA PAGAMENTO:\n\n';
    credoresMap.forEach((info) => {
      if (info.isGrupo) {
        textoPix += `- ${info.nome}\n`;
        const membrosComPix = info.membros.filter(m => m.chavePix);
        if (membrosComPix.length === 0) {
          textoPix += `   PIX: não cadastrada\n\n`;
          return;
        }
        membrosComPix.forEach((m) => {
          textoPix += `  ${m.nome}\n`;
          textoPix += `   PIX: ${m.chavePix}\n`;
          const link = getLinkParaChavePix(m.chavePix!);
          if (link) textoPix += `   Link: ${link}\n`;
          textoPix += `\n`;
        });
        return;
      }

      textoPix += `- ${info.nome}\n`;
      if (!info.chavePix) {
        textoPix += `   PIX: não cadastrada\n\n`;
        return;
      }
      const link = getLinkParaChavePix(info.chavePix);
      textoPix += `   PIX: ${info.chavePix}\n`;
      if (link) textoPix += `   Link: ${link}\n`;
      textoPix += `\n`;
    });
    textoPix += '--\n';
    return textoPix;
  };

  // Função para realizar o compartilhamento (chamada pelo modal)
  const realizarCompartilhamento = async () => {
    if (isSharing) {
      return;
    }

    setIsSharing(true);

    // Verificar se o racha está carregado
    if (!racha) {
      console.error('Racha não encontrado');
      Alert.alert('Erro', 'Dados do racha não encontrados. Tente novamente.');
      setIsSharing(false);
      return;
    }

    // Usar as transações já calculadas que foram passadas como props

    const textoPix = await gerarTextoPixParaPagamento();

    if (Platform.OS === 'web') {
      try {
        // Gerar imagem usando canvas com dados corretos
        const imageBlob = await generateBalanceImage(textoPix, racha, colors);

        // No web, vamos usar Web Share API se disponível, senão copiar texto
        if (navigator.share) {
          const file = new File([imageBlob], 'divisao-racha.png', { type: 'image/png' });

          await navigator.share({
            title: 'Divisão do Racha',
            text: textoPix,
            files: [file]
          });
        } else {
          // Fallback: copiar texto para área de transferência
          await navigator.clipboard.writeText(textoPix);
          Alert.alert('Texto copiado!', 'A divisão foi copiada para a área de transferência. Você pode colar em qualquer aplicativo de mensagem.');
        }
      } catch (err) {
        console.error('Erro no compartilhamento web:', err);
        try {
          // Último fallback: apenas copiar texto
          await navigator.clipboard.writeText(textoPix);
          Alert.alert('Texto copiado!', 'A divisão foi copiada para a área de transferência.');
        } catch {
          Alert.alert('Erro', 'Não foi possível compartilhar ou copiar a divisão.');
        }
      } finally {
        setIsSharing(false);
      }
      return;
    }

    // No mobile, compartilhe sempre a imagem + texto
    if (!viewShotRef.current || !(viewShotRef.current as any).capture) {
      console.warn('ViewShot ref não está disponível ou método capture ausente');
      Alert.alert('Erro', 'Não foi possível capturar a imagem do balanço.');
      setIsSharing(false);
      return;
    }

    try {
      // Aguardar um pouco para garantir que o componente está renderizado
      await new Promise(resolve => setTimeout(resolve, 100));

      const uri = await (viewShotRef.current as any).capture();

      try {
        await Share.share({
          title: 'Divisão do Racha',
          message: textoPix,
          url: uri,
        });
      } catch {
        await Sharing.shareAsync(uri, {
          mimeType: 'image/png',
          dialogTitle: 'Compartilhar divisão do racha',
          UTI: 'public.png',
        });
        await Clipboard.setStringAsync(textoPix);
        Alert.alert('Sucesso!', 'Imagem gerada. O texto com as chaves PIX foi copiado para a área de transferência.');
      }

    } catch (err) {
      logger.error('Erro ao compartilhar divisão');
      Alert.alert('Erro', 'Não foi possível compartilhar a divisão. Verifique o console para mais detalhes.');
    } finally {
      setIsSharing(false);
    }
  };

  // Componente exportável para a imagem - inclui TODAS as despesas
  const ExportDivisao = ({ transacoes: transacoesExport }: { transacoes: Transacao[] }) => {
    const totalDespesas = racha.despesas.reduce((total: number, despesa: any) => total + despesa.valor, 0);

    return (
      <View
        style={{ backgroundColor: '#131313', padding: 24, minWidth: 360, maxWidth: 420 }} // Fundo dark do mockup
        collapsable={false}
      >
        {/* Cabeçalho Melhorado */}
        <View style={{ alignItems: 'center', marginBottom: 24 }}>
          <Text style={{ fontSize: 24, fontFamily: 'Inter-Bold', color: '#e5e2e1', marginBottom: 4, letterSpacing: -0.5 }}>
            {racha.emoji} {racha.nome}
          </Text>
          <Text style={{ fontSize: 14, color: '#c1c6d7', marginBottom: 12, fontFamily: 'Inter-Medium' }}>
            {formatarData(racha.dataCriacao)}
          </Text>
          <Text style={{ fontSize: 22, fontFamily: 'Inter-Bold', color: '#e5e2e1' }}>
            Total: {formatarValor(totalDespesas)}
          </Text>
        </View>

        {/* Seção de Despesas */}
        {racha.despesas && racha.despesas.length > 0 && (
          <View style={{ marginBottom: 24 }}>
            <Text style={{ fontSize: 14, fontFamily: 'Inter-Bold', color: '#0059bb', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center' }}>
              DESPESAS
            </Text>
            {racha.despesas.map((despesa: any, idx: number) => (
              <View key={despesa.id || idx} style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingVertical: 12,
                borderBottomWidth: idx < racha.despesas.length - 1 ? 1 : 0,
                borderBottomColor: '#414754'
              }}>
                <View style={{ flex: 1, marginRight: 12 }}>
                  <Text style={{ fontSize: 16, fontFamily: 'Inter-Bold', color: '#e5e2e1', marginBottom: 4 }}>
                    {despesa.categoria?.emoji || '💰'} {despesa.titulo}
                  </Text>
                  <Text style={{ fontSize: 13, color: '#c1c6d7', fontFamily: 'Inter-Regular' }}>
                    Pago por: {despesa.pagoPorNome}
                  </Text>
                </View>
                <Text style={{ fontSize: 16, fontFamily: 'Inter-Medium', color: '#0059bb' }}>
                  {formatarValor(despesa.valor)}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Divisor */}
        <View style={{ height: 1, backgroundColor: '#414754', marginBottom: 24 }} />

        {/* Seção Quem Deve Para Quem */}
        <View style={{ marginBottom: 24 }}>
          <Text style={{ fontSize: 14, fontFamily: 'Inter-Bold', color: '#0059bb', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center' }}>
            💰 QUEM DEVE PARA QUEM
          </Text>
          {transacoesExport.length === 0 ? (
            <Text style={{ fontSize: 16, color: '#e5e2e1', textAlign: 'center', fontFamily: 'Inter-Medium' }}>Tudo quitado! 🎉</Text>
          ) : (
            transacoesExport.map((transacao: Transacao, idx: number) => {
              return (
                <View key={idx} style={{
                  marginBottom: 12,
                }}>
                  <Text style={{ fontSize: 15, fontFamily: 'Inter-Medium', color: '#e5e2e1' }}>
                    {transacao.devedorNome} deve {formatarValor(transacao.valor)} para {transacao.credorNome}
                  </Text>
                </View>
              );
            })
          )}
        </View>

      </View>
    );
  };

  return (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
      {(() => {
        if (transacoes.length === 0) {
          return (
            <View style={{ alignItems: 'center', justifyContent: 'center', padding: 32 }}>
              <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 8 }}>Tudo quitado! 🎉</Text>
              <Text style={{ fontSize: 14, color: '#888', textAlign: 'center' }}>
                Não há pendências financeiras neste racha
              </Text>
            </View>
          );
        }

        return (
          <View style={{ padding: 16 }}>
            <Text style={{ fontSize: 16, fontFamily: 'Inter-SemiBold', color: colors.text, marginBottom: 12 }}>Transações para quitar:</Text>
            {transacoes.map((transacao: Transacao, index: number) => {
              // Identificar se credor ou devedor é grupo
              const grupoCredor = racha.gruposDeBolso?.find((g: any) => g.id === transacao.credorId);
              const grupoDevedor = racha.gruposDeBolso?.find((g: any) => g.id === transacao.devedorId);
              const credor = participantes.find((p: Participante) => p.id === transacao.credorId);
              const devedor = participantes.find((p: Participante) => p.id === transacao.devedorId);
              return (
                <View key={index} style={{
                  marginBottom: 12,
                  backgroundColor: colors.surface,
                  borderRadius: 16,
                  paddingVertical: 16, // Aumentado padding vertical
                  paddingHorizontal: 20, // Aumentado padding horizontal
                  borderWidth: 1,
                  borderColor: colors.border,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 4 }, // Aumentado sombra
                  shadowOpacity: 0.05, // Sombra mais sutil
                  shadowRadius: 8,
                  elevation: 2
                }}>
                  {isNarrowScreen ? (
                    // Layout para telas estreitas (< 400px) - Duas linhas
                    <View style={{ gap: 8 }}>
                      {/* Linha 1: Devedor */}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}>
                        {grupoDevedor ? (
                          <>
                            <View style={{
                              width: 32,
                              height: 32,
                              borderRadius: 16,
                              backgroundColor: colors.surface,
                              borderWidth: 1,
                              borderColor: colors.border,
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: 12
                            }}>
                              <Users size={16} color={colors.textSecondary} />
                            </View>
                            <Text style={{
                              fontFamily: 'Inter-Medium',
                              color: colors.text,
                              fontSize: 14,
                              flex: 1
                            }}>
                              {grupoDevedor.nome}
                            </Text>
                          </>
                        ) : (
                          <>
                            {devedor && devedor.foto ? (
                              <Image
                                source={{ uri: devedor.foto }}
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 16,
                                  marginRight: 12
                                }}
                              />
                            ) : (
                              <View style={{
                                width: 32,
                                height: 32,
                                borderRadius: 16,
                                backgroundColor: colors.surface,
                                borderWidth: 1,
                                borderColor: colors.border,
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginRight: 12
                              }}>
                                <User size={16} color={colors.textSecondary} />
                              </View>
                            )}
                            <Text style={{
                              fontFamily: 'Inter-Medium',
                              color: colors.text,
                              fontSize: 14,
                              flex: 1
                            }}>
                              {transacao.devedorNome}
                            </Text>
                          </>
                        )}
                      </View>

                      {/* Linha 2: Seta e Valor e Credor */}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: colors.background,
                        padding: 8,
                        borderRadius: 12,
                        gap: 8
                      }}>
                         <Text style={{
                          fontFamily: 'Inter-Regular',
                          color: colors.textSecondary,
                          fontSize: 12,
                        }}>
                          paga
                        </Text>
                        <Text style={{
                          fontFamily: 'Inter-Bold',
                          color: colors.primary,
                          fontSize: 16,
                        }}>
                          {formatarValor(transacao.valor)}
                        </Text>
                         <Text style={{
                          fontFamily: 'Inter-Regular',
                          color: colors.textSecondary,
                          fontSize: 12,
                        }}>
                          para
                        </Text>
                         <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                          {grupoCredor ? (
                            <>
                              <Text style={{
                                fontFamily: 'Inter-SemiBold',
                                color: colors.text,
                                fontSize: 13,
                                marginRight: 8,
                                textAlign: 'right'
                              }}>
                                {grupoCredor.nome}
                              </Text>
                              <View style={{
                                width: 24,
                                height: 24,
                                borderRadius: 12,
                                backgroundColor: colors.success + '15',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}>
                                <Users size={12} color={colors.success} />
                              </View>
                            </>
                          ) : (
                            <>
                               <Text style={{
                                fontFamily: 'Inter-SemiBold',
                                color: colors.text,
                                fontSize: 13,
                                marginRight: 8,
                                textAlign: 'right'
                              }}>
                                {transacao.credorNome}
                              </Text>
                              {credor && credor.foto ? (
                                <Image
                                  source={{ uri: credor.foto }}
                                  style={{ width: 24, height: 24, borderRadius: 12 }}
                                />
                              ) : (
                                <View style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 12,
                                  backgroundColor: colors.success + '15',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}>
                                  <User size={12} color={colors.success} />
                                </View>
                              )}
                            </>
                          )}
                        </View>
                      </View>
                    </View>
                  ) : (
                    // Layout para telas largas (>= 400px) - Uma linha horizontal
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      {/* Devedor */}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexShrink: 1,
                        marginRight: 12
                      }}>
                        {grupoDevedor ? (
                          <>
                            <View style={{
                              width: 28,
                              height: 28,
                              borderRadius: 14,
                              backgroundColor: colors.primary + '20',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: 8
                            }}>
                              <Users size={14} color={colors.primary} />
                            </View>
                            <Text style={{
                              fontFamily: 'Inter-SemiBold',
                              color: colors.text,
                              fontSize: 14,
                              flexShrink: 1
                            }}>
                              {grupoDevedor.nome}
                            </Text>
                          </>
                        ) : (
                          <>
                            {devedor && devedor.foto ? (
                              <Image
                                source={{ uri: devedor.foto }}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 14,
                                  marginRight: 8
                                }}
                              />
                            ) : (
                              <View style={{
                                width: 28,
                                height: 28,
                                borderRadius: 14,
                                backgroundColor: colors.textSecondary + '20',
                                marginRight: 8
                              }} />
                            )}
                            <Text style={{
                              fontFamily: 'Inter-SemiBold',
                              color: colors.text,
                              fontSize: 14,
                              flexShrink: 1
                            }}>
                              {transacao.devedorNome}
                            </Text>
                          </>
                        )}
                      </View>

                      {/* Ação e Valor */}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginHorizontal: 8
                      }}>
                        <Text style={{
                          color: colors.textSecondary,
                          fontSize: 12,
                          fontFamily: 'Inter-Medium',
                          marginRight: 6
                        }}>paga</Text>
                        <Text style={{
                          fontFamily: 'Inter-Bold',
                          color: colors.primary,
                          fontSize: 16,
                          marginRight: 6
                        }}>
                          {formatarValor(transacao.valor)}
                        </Text>
                        <Text style={{
                          color: colors.textSecondary,
                          fontSize: 12,
                          fontFamily: 'Inter-Medium'
                        }}>para</Text>
                      </View>

                      {/* Credor e PIX */}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexShrink: 1
                      }}>
                        {grupoCredor ? (
                          <>
                            <View style={{
                              width: 28,
                              height: 28,
                              borderRadius: 14,
                              backgroundColor: colors.success + '20',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: 8
                            }}>
                              <Users size={14} color={colors.success} />
                            </View>
                            <Text style={{
                              fontFamily: 'Inter-SemiBold',
                              color: colors.text,
                              fontSize: 14,
                              flexShrink: 1
                            }}>
                              {grupoCredor.nome}
                            </Text>
                          </>
                        ) : (
                          <>
                            {credor && credor.foto ? (
                              <Image
                                source={{ uri: credor.foto }}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 14,
                                  marginRight: 8
                                }}
                              />
                            ) : (
                              <View style={{
                                width: 28,
                                height: 28,
                                borderRadius: 14,
                                backgroundColor: colors.textSecondary + '20',
                                marginRight: 8
                              }} />
                            )}
                            <Text style={{
                              fontFamily: 'Inter-SemiBold',
                              color: colors.text,
                              fontSize: 14,
                              flexShrink: 1
                            }}>
                              {transacao.credorNome}
                            </Text>
                          </>
                        )}

                        {/* Botão PIX */}
                        {!grupoCredor && credor && credor.chavePix && credor.chavePix.valor && (
                          <TouchableOpacity
                            style={{
                              backgroundColor: colors.success + '20',
                              paddingHorizontal: 12,
                              paddingVertical: 6,
                              borderRadius: 12,
                              marginLeft: 8,
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 4
                            }}
                            onPress={() => copiarChavePix(credor.chavePix!.valor)}
                            activeOpacity={0.8}
                          >
                            <Text style={{
                              color: colors.success,
                              fontFamily: 'Inter-SemiBold',
                              fontSize: 12
                            }}>Copiar PIX</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  )}

                </View>
              );
            })}
            {/* Botão de compartilhar divisão */}
            <TouchableOpacity
              style={{ marginTop: 24, backgroundColor: colors.primary, borderRadius: 8, padding: 14, alignItems: 'center', flexDirection: 'row', justifyContent: 'center' }}
              onPress={compartilhar}
              disabled={isSharing}
              activeOpacity={0.8}
            >
              <ShareIcon size={20} color={'#fff'} style={{ marginRight: 8 }} />
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>
                {isSharing ? 'Compartilhando...' : 'Compartilhar divisão'}
              </Text>
            </TouchableOpacity>

            {/* Anúncio para usuários não-premium */}
            {!isPremium() && (
              <PremiumCTA />
            )}

            {/* View invisível para exportação - Posicionada fora da tela para evitar corte */}
            {/* Modal de Preview para Compartilhamento (Visível para garantir renderização) */}
            <Modal
              visible={showSharePreview}
              transparent={true}
              animationType="slide"
              onRequestClose={() => setShowSharePreview(false)}
            >
              <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', justifyContent: 'center' }}>
                <View style={{ flex: 1, margin: 20, backgroundColor: colors.background, borderRadius: 16, overflow: 'hidden' }}>
                  {/* Header do Modal */}
                  <View style={{ padding: 16, borderBottomWidth: 1, borderBottomColor: colors.border, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 18, fontWeight: 'bold', color: colors.text }}>Pré-visualização</Text>
                    <TouchableOpacity onPress={() => setShowSharePreview(false)}>
                      <X size={24} color={colors.text} />
                    </TouchableOpacity>
                  </View>

                  {/* Conteúdo Scrollável para Captura */}
                  <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                    <ViewShot ref={viewShotRef} options={{ format: 'png', quality: 0.9, result: 'tmpfile' }}>
                      <ExportDivisao transacoes={transacoes} />
                    </ViewShot>
                  </ScrollView>

                  {/* Footer com Botão de Ação */}
                  <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: colors.border }}>
                    <TouchableOpacity
                      style={{
                        backgroundColor: colors.primary,
                        padding: 16,
                        borderRadius: 8,
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: 'center'
                      }}
                      onPress={realizarCompartilhamento}
                      disabled={isSharing}
                    >
                      {isSharing ? (
                        <ActivityIndicator color="#fff" style={{ marginRight: 8 }} />
                      ) : (
                        <ShareIcon size={20} color="#fff" style={{ marginRight: 8 }} />
                      )}
                      <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>
                        {isSharing ? 'Compartilhando...' : 'Confirmar e Compartilhar'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </Modal>
          </View>
        );
      })()}

    </ScrollView>
  );
}

export default function RachaDetailsScreen() {
  const { id } = useLocalSearchParams();
  const { user, isPremium } = useAuth();
  const { getRacha, addDespesa, updateDespesa, addParticipanteToRacha, removeParticipanteFromRacha, finalizarRacha, reativarRacha, deleteDespesa, getTotalDespesas, updateRacha } = useRacha();
  const { getAllParticipantes, updateParticipanteInAllGrupos } = useGrupos();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<'despesas' | 'balanco' | 'consolidado' | 'participantes'>('despesas');
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showEditExpenseModal, setShowEditExpenseModal] = useState(false);
  const [showAddParticipanteModal, setShowAddParticipanteModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // Estados para adicionar despesa
  const [novaDespesaTitulo, setNovaDespesaTitulo] = useState('');
  const [novaDespesaValor, setNovaDespesaValor] = useState('');
  const [novaDespesaPagoPor, setNovaDespesaPagoPor] = useState(user?.id || '');
  const [participantesSelecionados, setParticipantesSelecionados] = useState<string[]>([]);
  const [modoDivisao, setModoDivisao] = useState<ModoDivisao>('igual');
  const [divisoesPersonalizadas, setDivisoesPersonalizadas] = useState<DivisaoPersonalizada[]>([]);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<Categoria>(categoriasPadrao?.[0] || { id: 'outros', nome: 'Outros', emoji: '📦', cor: '#6B7280' });

  // Estados para date picker
  const [novaDespesaData, setNovaDespesaData] = useState<Date>(new Date());
  // showDatePicker removed
  const [editDespesaData, setEditDespesaData] = useState<Date>(new Date());
  const [showEditDatePicker, setShowEditDatePicker] = useState(false);

  // Estados para editar despesa
  const [editingDespesa, setEditingDespesa] = useState<any>(null);
  const [editDespesaTitulo, setEditDespesaTitulo] = useState('');
  const [editDespesaValor, setEditDespesaValor] = useState('');
  const [editDespesaPagoPor, setEditDespesaPagoPor] = useState('');
  const [editParticipantesSelecionados, setEditParticipantesSelecionados] = useState<string[]>([]);
  const [editModoDivisao, setEditModoDivisao] = useState<ModoDivisao>('igual');
  const [editDivisoesPersonalizadas, setEditDivisoesPersonalizadas] = useState<DivisaoPersonalizada[]>([]);
  const [editCategoriaSelecionada, setEditCategoriaSelecionada] = useState<Categoria>(categoriasPadrao?.[0] || { id: 'outros', nome: 'Outros', emoji: '📦', cor: '#6B7280' });

  // Estados para adicionar participante
  const [novoParticipante, setNovoParticipante] = useState({ nome: '', email: '', chavePixPadrao: '' });
  const [participantesDisponiveis, setParticipantesDisponiveis] = useState<any[]>([]);

  // Estados para alertas nativos
  const [showNativeAlert, setShowNativeAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    onConfirm?: () => void | Promise<void>;
    showCancel?: boolean;
  }>({ title: '', message: '' });

  // Estados para editar chave PIX
  const [showEditPixModal, setShowEditPixModal] = useState(false);
  const [editingParticipante, setEditingParticipante] = useState<any>(null);
  const [editPixChave, setEditPixChave] = useState<any>(null);

  // Estados para filtros
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [showFiltrosModal, setShowFiltrosModal] = useState(false);

  // Estados para vínculos de bolso
  const [showGrupoDeBolsoModal, setShowGrupoDeBolsoModal] = useState(false);
  const [showInfoGrupoDeBolsoModal, setShowInfoGrupoDeBolsoModal] = useState(false);

  // Estados para banner intersticial
  const [showBannerIntersticial, setShowBannerIntersticial] = useState(false);
  const [novoGrupoDeBolsoNome, setNovoGrupoDeBolsoNome] = useState('');
  const [grupoEditando, setGrupoEditando] = useState<GrupoDeBolso | null>(null);
  const [grupoDeBolsoSelecionado, setGrupoDeBolsoSelecionado] = useState<string | null>(null);

  const racha = getRacha(id as string);
  const todosParticipantes = useMemo(() => getAllParticipantes(), [getAllParticipantes]);

  // Log de debug para verificar se o componente está sendo renderizado
  // Executar testes de debug quando o componente for montado
  useEffect(() => {
    if (__DEV__) {
      testBalanceCalculator();
      testEqualDivision();
      testManyParticipants();
      testDataTypeIssues();
    }
  }, []);

  // Controle de sessão para banner intersticial - apenas na aba Balanço
  useEffect(() => {
    const checkBannerStatus = async () => {
      try {
        // Se não estiver na aba Balanço, esconder o banner se estiver sendo exibido
        if (activeTab !== 'balanco' && showBannerIntersticial) {
          setShowBannerIntersticial(false);
          return;
        }

        // Verificar se o usuário não é premium E está na aba Balanço
        if (!isPremium() && activeTab === 'balanco') {
          // Para desenvolvimento: limpar o sessionStorage para testar o banner
          if (__DEV__ && Platform.OS === 'web') {
            sessionStorage.removeItem('bannerIntersticialShown');
          }

          let bannerShown = false;

          if (Platform.OS === 'web') {
            // Para web, usar sessionStorage
            bannerShown = sessionStorage.getItem('bannerIntersticialShown') === 'true';
          } else {
            // Para mobile, usar AsyncStorage
            const stored = await AsyncStorage.getItem('bannerIntersticialShown');
            bannerShown = stored === 'true';
          }

          if (!bannerShown && !showBannerIntersticial) {
            // Mostrar o banner após um pequeno delay
            const timer = setTimeout(async () => {
              setShowBannerIntersticial(true);
              // Marcar como mostrado nesta sessão
              if (Platform.OS === 'web') {
                sessionStorage.setItem('bannerIntersticialShown', 'true');
              } else {
                await AsyncStorage.setItem('bannerIntersticialShown', 'true');
              }
            }, 1000);

            return () => clearTimeout(timer);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status do banner:', error);
      }
    };

    checkBannerStatus();
  }, [isPremium, activeTab]);

  // Auto-fechar banner após 10 segundos
  useEffect(() => {
    if (showBannerIntersticial) {
      const timer = setTimeout(async () => {
        setShowBannerIntersticial(false);
        // Garantir que está marcado como mostrado quando fechar automaticamente
        if (Platform.OS === 'web') {
          sessionStorage.setItem('bannerIntersticialShown', 'true');
        } else {
          await AsyncStorage.setItem('bannerIntersticialShown', 'true');
        }
      }, 10000); // 10 segundos

      return () => clearTimeout(timer);
    }
  }, [showBannerIntersticial]);

  const handleCloseBanner = () => {
    setShowBannerIntersticial(false);
  };

  // Filtrar e ordenar despesas por categoria e data
  const despesasFiltradas = useMemo(() => {
    if (!racha) return [];

    let despesas = [...racha.despesas];

    // Filtrar por categoria
    if (categoriaFiltro) {
      despesas = despesas.filter(d => d.categoria?.id === categoriaFiltro);
    }

    // Ordenar: mais recentes primeiro
    return despesas.sort((a, b) => {
      const getTimestamp = (data: any): number => {
        if (!data) return 0;
        if (data instanceof Date) return data.getTime();
        if (typeof data === 'object' && 'seconds' in data) return data.seconds * 1000;
        if (typeof data === 'object' && typeof data.toDate === 'function') return data.toDate().getTime();
        return new Date(data).getTime() || 0;
      };
      return getTimestamp(b.data) - getTimestamp(a.data);
    });
  }, [racha, categoriaFiltro]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    errorText: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      marginBottom: 16,
    },
    backText: {
      fontSize: 16,
      fontFamily: 'Inter-Medium',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      gap: 16,
    },
    backButton: {
      padding: 4,
    },
    headerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      gap: 12,
    },
    headerEmoji: {
      fontSize: 32,
    },
    headerText: {
      flex: 1,
    },
    headerTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 4,
    },
    headerMetadata: {
      marginBottom: 4,
    },
    participantesContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    statusBadge: {
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
    },
    statusBadgeAtivo: {
      backgroundColor: colors.success + '20',
    },
    statusBadgeFinalizado: {
      backgroundColor: colors.textSecondary + '20',
    },
    statusBadgeText: {
      fontSize: 10,
      fontFamily: 'Inter-SemiBold',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    statusBadgeTextAtivo: {
      color: colors.success,
    },
    statusBadgeTextFinalizado: {
      color: colors.textSecondary,
    },
    headerSubtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    headerTotal: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    statusActions: {
      flexDirection: 'row',
      gap: 8,
    },
    statusButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    finalizarButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.success + '20',
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 20,
      gap: 6,
    },
    finalizarButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.success,
    },
    finalizarButtonCompact: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.success + '20',
      paddingHorizontal: 8,
      paddingVertical: 6,
      borderRadius: 16,
      gap: 4,
    },
    finalizarButtonCompactText: {
      fontSize: 11,
      fontFamily: 'Inter-SemiBold',
      color: colors.success,
    },
    tabs: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tab: {
      flex: 1,
      paddingVertical: 16,
      alignItems: 'center',
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    activeTab: {
      borderBottomColor: colors.primary,
    },
    tabText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.textSecondary,
    },
    activeTabText: {
      color: colors.primary,
    },
    tabContent: {
      flex: 1,
      paddingHorizontal: 24,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 80,
    },
    emptyTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    emptySubtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 20,
    },
    despesasList: {
      paddingVertical: 16,
      gap: 12,
    },
    despesaCard: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: colors.border,
    },
    despesaHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
    },
    despesaTituloWrapper: {
      flex: 1,
      marginRight: 12,
    },
    despesaMetadados: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      marginBottom: 12,
    },
    metadadoItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    metadadoTexto: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    despesaTitulo: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      flexShrink: 1,
    },
    despesaValor: {
      fontSize: 16,
      fontFamily: 'Inter-Bold',
      color: colors.primary,
    },
    despesaActions: {
      flexDirection: 'row',
      gap: 8,
    },
    despesaActionButton: {
      padding: 6,
      borderRadius: 6,
      backgroundColor: colors.background,
    },
    despesaDetails: {
      flexDirection: 'row',
      gap: 16,
      flexWrap: 'wrap',
    },
    despesaDetailItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 1,
    },
    despesaData: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      flexShrink: 1,
    },
    despesaPagador: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      flexShrink: 1,
    },
    despesaFoto: {
      width: '100%',
      height: 120,
      borderRadius: 12,
      marginTop: 12,
    },
    balancoList: {
      paddingVertical: 16,
      gap: 16,
    },
    balancoHeader: {
      marginBottom: 8,
    },
    balancoTitle: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    transacaoCard: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: colors.border,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    transacaoInfo: {
      flex: 1,
    },
    transacaoTexto: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      lineHeight: 20,
    },
    transacaoPixButton: {
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
    },
    transacaoPixButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    participantesList: {
      paddingVertical: 16,
      gap: 12,
    },
    participanteCard: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: colors.border,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    participanteInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      gap: 12,
    },
    participanteFoto: {
      width: 48,
      height: 48,
      borderRadius: 24,
    },
    participanteDetails: {
      flex: 1,
    },
    participanteNome: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 2,
    },
    participantePixKey: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    participanteActions: {
      flexDirection: 'row',
      gap: 8,
    },
    pixButton: {
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    pixButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    inviteButton: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 2,
      borderColor: colors.primary,
      borderStyle: 'dashed',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    inviteButtonText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    fab: {
      position: 'absolute',
      bottom: 24,
      right: 24,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      elevation: 4,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
    },
    addParticipanteButton: {
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 16,
      paddingHorizontal: 24,
      marginTop: 24,
      alignItems: 'center',
      justifyContent: 'center',
    },
    addParticipanteButtonText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
    },
    modalContainer: {
      flex: 1,
      backgroundColor: colors.background,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    modalCancelButton: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    modalCreateButton: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    modalContent: {
      flex: 1,
      paddingHorizontal: 24,
      paddingTop: 24,
    },
    inputGroup: {
      marginBottom: 24,
    },
    inputLabel: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    inputSubLabel: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginBottom: 12,
    },
    input: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 48,
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      borderWidth: 1,
      borderColor: colors.border,
    },
    pagadorList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    pagadorOption: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 12,
      borderWidth: 2,
      borderColor: colors.border,
      gap: 8,
    },
    pagadorSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    pagadorFoto: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    pagadorNome: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    pagadorNomeSelected: {
      color: colors.primary,
    },
    participantesSelection: {
      gap: 8,
    },
    participanteOption: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 2,
      borderColor: colors.border,
      gap: 12,
    },
    participanteSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    participanteFotoSmall: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    participanteOptionNome: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      flex: 1,
    },
    participanteOptionNomeSelected: {
      color: colors.primary,
    },
    checkbox: {
      width: 24,
      height: 24,
      borderRadius: 12,
      borderWidth: 2,
      borderColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
    },
    checkboxSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },
    inviteContent: {
      flex: 1,
      paddingHorizontal: 24,
      paddingTop: 24,
      gap: 16,
    },
    inviteOption: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 16,
    },
    inviteOptionText: {
      fontSize: 16,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    participantesDisponiveis: {
      gap: 12,
      marginBottom: 24,
    },
    participanteDisponivel: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 12,
    },
    addButton: {
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
    },
    addButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginVertical: 16,
    },
    dividerText: {
      textAlign: 'center',
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      backgroundColor: colors.background,
      paddingHorizontal: 16,
      marginTop: -8,
    },
    optionalLabel: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginTop: 4,
    },
    qrCodeContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
    },
    qrCodeTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 20,
      textAlign: 'center',
    },
    qrCodeSubtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginTop: 20,
      textAlign: 'center',
      lineHeight: 20,
    },
    alertOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      elevation: 9999,
    },
    alertContainer: {
      backgroundColor: colors.surface,
      padding: 24,
      borderRadius: 16,
      width: '80%',
      maxHeight: '80%',
      justifyContent: 'space-between',
    },
    alertTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    alertMessage: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      marginBottom: 24,
    },
    alertButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    alertButton: {
      padding: 12,
      borderRadius: 8,
      backgroundColor: colors.primary + '20',
    },
    alertButtonCancel: {
      backgroundColor: colors.error + '20',
    },
    alertButtonCancelText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.error,
    },
    alertButtonConfirm: {
      backgroundColor: colors.success + '20',
    },
    alertButtonConfirmText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.success,
    },
    editPixButton: {
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    editPixButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    removeParticipanteButton: {
      backgroundColor: colors.error + '20',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    removeParticipanteButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.error,
    },
    divisaoDetails: {
      marginTop: 16,
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    divisaoTitle: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      marginBottom: 12,
    },
    divisaoItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingVertical: 4,
    },
    divisaoFoto: {
      width: 24,
      height: 24,
      borderRadius: 12,
    },
    divisaoNome: {
      flex: 1,
      fontSize: 14,
      fontFamily: 'Inter-Medium',
    },
    divisaoValor: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
    },
    categoriaBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      gap: 4,
    },
    categoriaEmoji: {
      fontSize: 12,
    },
    categoriaNome: {
      fontSize: 10,
      fontFamily: 'Inter-SemiBold',
    },
    filtrosContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
    },
    filtroButton: {
      padding: 12,
      borderRadius: 8,
      backgroundColor: colors.primary + '20',
    },
    filtroButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    limparFiltroButton: {
      padding: 12,
      borderRadius: 8,
      backgroundColor: colors.error + '20',
    },
    limparFiltroButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.error,
    },
    categoriasGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    categoriaOption: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 12,
      borderRadius: 16,
      borderWidth: 2,
      borderColor: colors.border,
      gap: 8,
    },
    categoriaOptionSelected: {
      borderColor: colors.primary,
    },
    categoriaOptionEmoji: {
      fontSize: 16,
    },
    categoriaOptionNome: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
    },
    categoriaOptionNomeSelected: {
      color: colors.primary,
    },
  });

  useEffect(() => {
    if (racha) {
      setParticipantesSelecionados(racha.participantes.map(p => p.id));
      setNovaDespesaPagoPor(user?.id || (racha.participantes && racha.participantes.length > 0 ? racha.participantes[0]?.id : '') || '');

      // Filtrar participantes que não estão no racha
      const participantesNaoNoRacha = todosParticipantes.filter(p =>
        !racha.participantes.some(rp => rp.id === p.id)
      );
      setParticipantesDisponiveis(participantesNaoNoRacha);
    }
  }, [racha, user, todosParticipantes]);

  if (!racha) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, { color: colors.text }]}>Racha não encontrado</Text>
          <TouchableOpacity onPress={() => router.back()}>
            <Text style={[styles.backText, { color: colors.primary }]}>Voltar</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);
  };

  const formatarData = (data: Date | string | any) => {
    try {
      let dataObj: Date;

      // Verificar se é um Timestamp do Firestore
      if (data && typeof data === 'object' && 'seconds' in data && 'nanoseconds' in data) {
        dataObj = new Date(data.seconds * 1000);
      } else if (data && typeof data === 'object' && typeof data.toDate === 'function') {
        dataObj = data.toDate();
      } else if (data instanceof Date) {
        dataObj = data;
      } else if (typeof data === 'string') {
        dataObj = new Date(data);
      } else {
        console.warn('Formato de data não reconhecido:', data);
        return 'Data inválida';
      }

      // Verificar se a data é válida
      if (isNaN(dataObj.getTime())) {
        console.error('Data inválida:', data);
        return 'Data inválida';
      }

      return dataObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error, data);
      return 'Data inválida';
    }
  };

  // Função para formatar data curta (ex: "15 Out")
  const formatarDataCurta = (data: Date | string | any): string => {
    try {
      const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      let dataObj: Date;

      if (data && typeof data === 'object' && 'seconds' in data && 'nanoseconds' in data) {
        dataObj = new Date(data.seconds * 1000);
      } else if (data && typeof data === 'object' && typeof data.toDate === 'function') {
        dataObj = data.toDate();
      } else if (data instanceof Date) {
        dataObj = data;
      } else if (typeof data === 'string') {
        dataObj = new Date(data);
      } else {
        return 'Data';
      }

      if (isNaN(dataObj.getTime())) return 'Data';

      return `${dataObj.getDate()} ${meses[dataObj.getMonth()]}`;
    } catch {
      return 'Data';
    }
  };

  // Adaptar chamada do cálculo de balanço para passar gruposDeBolso
  const calcularBalanco = () => {
    return calculateBalance(
      racha.despesas,
      racha.participantes,
      Array.isArray(racha.gruposDeBolso) ? racha.gruposDeBolso : []
    );
  };

  const adicionarDespesa = async () => {
    if (!novaDespesaTitulo.trim() || !novaDespesaValor.trim()) {
      showAlert('Erro', 'Preencha todos os campos obrigatórios', undefined, false);
      return;
    }

    if (participantesSelecionados.length === 0) {
      showAlert('Erro', 'Selecione pelo menos um participante', undefined, false);
      return;
    }

    const valor = parseFloat(novaDespesaValor.replace(',', '.'));
    if (isNaN(valor) || valor <= 0) {
      showAlert('Erro', 'Digite um valor válido', undefined, false);
      return;
    }

    // Validar divisão personalizada se necessário
    if (modoDivisao === 'personalizado') {
      const totalCalculado = divisoesPersonalizadas.reduce((total, divisao) => total + divisao.valorDevido, 0);
      const diferenca = Math.abs(valor - totalCalculado);

      if (diferenca > 0.01) {
        showAlert('Erro', 'A soma dos valores personalizados deve ser igual ao valor total da despesa', undefined, false);
        return;
      }
    }

    const pagoPorNome = racha.participantes.find(p => p.id === novaDespesaPagoPor)?.nome || '';

    // Preparar divisões baseado no modo
    let divisoes: any[] = [];
    if (modoDivisao === 'igual') {
      const valorPorPessoa = valor / participantesSelecionados.length;
      divisoes = participantesSelecionados.map(usuarioId => ({
        usuarioId,
        valorDevido: valorPorPessoa,
      }));
    } else {
      divisoes = divisoesPersonalizadas.map(divisao => ({
        usuarioId: divisao.usuarioId,
        valorDevido: divisao.valorDevido,
      }));
    }

    const novaDespesa: any = {
      titulo: novaDespesaTitulo.trim(),
      valor,
      data: novaDespesaData,
      pagoPorId: novaDespesaPagoPor,
      pagoPorNome,
      divisoes,
      modoDivisao,
      categoria: categoriaSelecionada,
    };

    // Adicionar divisoesPersonalizadas apenas se for modo personalizado
    if (modoDivisao === 'personalizado') {
      novaDespesa.divisoesPersonalizadas = divisoesPersonalizadas;
    }

    try {
      await addDespesa(racha.id, novaDespesa);

      // Limpar formulário
      setNovaDespesaTitulo('');
      setNovaDespesaValor('');
      setNovaDespesaPagoPor(user?.id || (racha.participantes && racha.participantes.length > 0 ? racha.participantes[0]?.id : '') || '');
      setParticipantesSelecionados(racha.participantes.map(p => p.id));
      setModoDivisao('igual');
      setDivisoesPersonalizadas([]);
      setCategoriaSelecionada(categoriasPadrao?.[0] || { id: 'outros', nome: 'Outros', emoji: '📦', cor: '#6B7280' });
      setShowAddExpenseModal(false);
      showAlert('Sucesso', 'Despesa adicionada com sucesso!', undefined, false);
    } catch {
      showAlert('Erro', 'Falha ao adicionar despesa', undefined, false);
    }
  };

  // Função de alert que funciona no web e mobile
  const showAlert = (title: string, message: string, onConfirm?: () => void, showCancel: boolean = true) => {
    setAlertConfig({ title, message, onConfirm, showCancel });
    setShowNativeAlert(true);
  };

  const confirmarExclusaoDespesa = (despesaId: string, despesaTitulo: string) => {
    showAlert(
      'Excluir Despesa',
      `Tem certeza que deseja excluir "${despesaTitulo}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          await deleteDespesa(racha.id, despesaId);
          showAlert('Sucesso', 'Despesa excluída com sucesso!');
        } catch {
          showAlert('Erro', 'Falha ao excluir despesa');
        }
      }
    );
  };

  const abrirEdicaoDespesa = (despesa: any) => {
    setEditingDespesa(despesa);
    setEditDespesaTitulo(despesa.titulo);
    setEditDespesaValor(despesa.valor.toString());
    setEditDespesaPagoPor(despesa.pagoPorId);
    setEditParticipantesSelecionados(despesa.divisoes.map((d: any) => d.usuarioId));
    setEditModoDivisao(despesa.modoDivisao || 'igual');
    setEditCategoriaSelecionada(despesa.categoria || categoriasPadrao?.[0] || { id: 'outros', nome: 'Outros', emoji: '📦', cor: '#6B7280' });

    // Carregar divisões personalizadas se existirem
    if (despesa.divisoesPersonalizadas && despesa.modoDivisao === 'personalizado') {
      setEditDivisoesPersonalizadas(despesa.divisoesPersonalizadas);
    } else {
      // Se não há divisões personalizadas, criar baseado nas divisões atuais
      const divisoesPersonalizadas = despesa.divisoes.map((divisao: any) => {
        const participante = racha.participantes.find(p => p.id === divisao.usuarioId);
        return {
          usuarioId: divisao.usuarioId,
          valorDevido: divisao.valorDevido,
          nome: participante?.nome || 'Participante'
        };
      });
      setEditDivisoesPersonalizadas(divisoesPersonalizadas);
    }

    setShowEditExpenseModal(true);
  };

  const editarDespesa = async () => {
    if (!editDespesaTitulo.trim() || !editDespesaValor.trim()) {
      Alert.alert('Erro', 'Preencha todos os campos obrigatórios');
      return;
    }

    if (editParticipantesSelecionados.length === 0) {
      Alert.alert('Erro', 'Selecione pelo menos um participante');
      return;
    }

    const valor = parseFloat(editDespesaValor.replace(',', '.'));
    if (isNaN(valor) || valor <= 0) {
      Alert.alert('Erro', 'Digite um valor válido');
      return;
    }

    // Validar divisão personalizada se necessário
    if (editModoDivisao === 'personalizado') {
      const totalCalculado = editDivisoesPersonalizadas.reduce((total, divisao) => total + divisao.valorDevido, 0);
      const diferenca = Math.abs(valor - totalCalculado);

      if (diferenca > 0.01) {
        Alert.alert('Erro', 'A soma dos valores personalizados deve ser igual ao valor total da despesa');
        return;
      }
    }

    const pagoPorNome = racha.participantes.find(p => p.id === editDespesaPagoPor)?.nome || '';

    // Preparar divisões baseado no modo
    let divisoes: any[] = [];
    if (editModoDivisao === 'igual') {
      const valorPorPessoa = valor / editParticipantesSelecionados.length;
      divisoes = editParticipantesSelecionados.map(usuarioId => ({
        usuarioId,
        valorDevido: valorPorPessoa,
      }));
    } else {
      divisoes = editDivisoesPersonalizadas.map(divisao => ({
        usuarioId: divisao.usuarioId,
        valorDevido: divisao.valorDevido,
      }));
    }

    const despesaAtualizada: any = {
      titulo: editDespesaTitulo.trim(),
      valor,
      pagoPorId: editDespesaPagoPor,
      pagoPorNome,
      divisoes,
      modoDivisao: editModoDivisao,
      categoria: editCategoriaSelecionada,
    };

    // Adicionar divisoesPersonalizadas apenas se for modo personalizado
    if (editModoDivisao === 'personalizado') {
      despesaAtualizada.divisoesPersonalizadas = editDivisoesPersonalizadas;
    }

    try {
      await updateDespesa(racha.id, editingDespesa.id, despesaAtualizada);

      // Limpar formulário
      setEditingDespesa(null);
      setEditDespesaTitulo('');
      setEditDespesaValor('');
      setEditDespesaPagoPor('');
      setEditParticipantesSelecionados([]);
      setEditModoDivisao('igual');
      setEditDivisoesPersonalizadas([]);
      setEditCategoriaSelecionada(categoriasPadrao?.[0] || { id: 'outros', nome: 'Outros', emoji: '📦', cor: '#6B7280' });
      setShowEditExpenseModal(false);
      Alert.alert('Sucesso', 'Despesa atualizada com sucesso!');
    } catch {
      Alert.alert('Erro', 'Falha ao atualizar despesa');
    }
  };

  const toggleEditParticipante = (participanteId: string) => {
    setEditParticipantesSelecionados(prev => {
      if (prev.includes(participanteId)) {
        return prev.filter(id => id !== participanteId);
      } else {
        return [...prev, participanteId];
      }
    });
  };

  const adicionarParticipanteExistente = async (participante: any) => {
    try {
      await addParticipanteToRacha(racha.id, participante);
      Alert.alert('Sucesso', `${participante.nome} foi adicionado ao racha!`);
    } catch {
      Alert.alert('Erro', 'Falha ao adicionar participante');
    }
  };

  const adicionarNovoParticipante = async () => {
    if (!novoParticipante.nome.trim()) {
      Alert.alert('Erro', 'Digite o nome do participante');
      return;
    }

    const participante: any = {
      id: Date.now().toString(),
      nome: novoParticipante.nome.trim(),
      foto: 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
    };

    // Adicionar chavePix apenas se fornecida
    if (novoParticipante.chavePixPadrao.trim()) {
      participante.chavePix = {
        tipo: 'aleatoria' as const,
        valor: novoParticipante.chavePixPadrao.trim()
      };
    }

    // Adicionar email apenas se fornecido
    if (novoParticipante.email.trim()) {
      participante.email = novoParticipante.email.trim();
    }

    try {
      await addParticipanteToRacha(racha.id, participante);
      setNovoParticipante({ nome: '', email: '', chavePixPadrao: '' });
      setShowAddParticipanteModal(false);
      Alert.alert('Sucesso', `${participante.nome} foi adicionado ao racha!`);
    } catch {
      Alert.alert('Erro', 'Falha ao adicionar participante');
    }
  };

  const toggleParticipante = (participanteId: string) => {
    setParticipantesSelecionados(prev => {
      if (prev.includes(participanteId)) {
        return prev.filter(id => id !== participanteId);
      } else {
        return [...prev, participanteId];
      }
    });
  };

  const copiarChavePix = async (chave: string) => {
    try {
      if (chave && chave.trim()) {
        await Clipboard.setStringAsync(chave.trim());
        showAlert('Copiado!', `Chave PIX copiada: ${chave.trim()}`, undefined, false);
      } else {
        showAlert('Aviso', 'Este participante não possui chave PIX cadastrada', undefined, false);
      }
    } catch {
      showAlert('Erro', 'Falha ao copiar chave PIX', undefined, false);
    }
  };



  const confirmarFinalizacao = () => {
    showAlert(
      'Finalizar Racha',
      `Tem certeza que deseja finalizar "${racha.nome}"? O racha será movido para "Rachas Finalizados" e não poderá ser reativado.`,
      async () => {
        try {
          await finalizarRacha(racha.id);
          // ShowAlert removed to prevent blocking window.alert, rely on navigation
          if (Platform.OS !== 'web') {
            // Optional toast for mobile could go here
          }
          router.replace('/(tabs)/rachas/'); // Replace history to avoid back-nav issues
        } catch {
          showAlert('Erro', 'Falha ao finalizar racha');
        }
      }
    );
  };

  const confirmarReativacao = () => {
    Alert.alert(
      'Reativar Racha',
      `Tem certeza que deseja reativar "${racha.nome}"?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Reativar',
          onPress: async () => {
            try {
              await reativarRacha(racha.id);
              Alert.alert('Sucesso', 'Racha reativado com sucesso!');
            } catch {
              Alert.alert('Erro', 'Falha ao reativar racha');
            }
          }
        }
      ]
    );
  };

  const renderDespesas = () => (
    <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
      {/* Filtros */}
      <View style={styles.filtrosContainer}>
        <TouchableOpacity
          style={styles.filtroButton}
          onPress={() => setShowFiltrosModal(true)}
          activeOpacity={0.7}
        >
          <Text style={styles.filtroButtonText}>
            {categoriaFiltro ? 'Filtro ativo' : 'Filtrar por categoria'}
          </Text>
        </TouchableOpacity>

        {categoriaFiltro && (
          <TouchableOpacity
            style={styles.limparFiltroButton}
            onPress={() => setCategoriaFiltro('')}
            activeOpacity={0.7}
          >
            <Text style={styles.limparFiltroButtonText}>Limpar</Text>
          </TouchableOpacity>
        )}
      </View>

      {Platform.OS === 'web' && !isPremium() ? (
        <View style={{ paddingHorizontal: 16, paddingBottom: 8, height: 90, maxHeight: 90, overflow: 'hidden' as any }}>
          <AdSenseBanner
            adSlot="2112588249"
            adKey={`racha-${String(id)}-despesas`}
            format="horizontal"
            fullWidthResponsive={false}
            style={{ height: 90, maxHeight: 90 }}
          />
        </View>
      ) : null}

      {despesasFiltradas.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>
            {categoriaFiltro ? 'Nenhuma despesa nesta categoria' : 'Nenhuma despesa ainda'}
          </Text>
          <Text style={styles.emptySubtitle}>
            {categoriaFiltro
              ? 'Tente selecionar outra categoria ou limpar o filtro'
              : 'Adicione a primeira despesa para começar a dividir os custos'
            }
          </Text>
        </View>
      ) : (
        <View style={styles.despesasList}>
          {despesasFiltradas.map((despesa) => (
            <View key={despesa.id} style={{
              backgroundColor: colors.surface,
              borderRadius: 16,
              padding: 16,
              marginBottom: 12,
              borderWidth: 1,
              borderColor: colors.border,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 2
            }}>
              {/* Cabeçalho: Título e Ações */}
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center', // Changed to center
                marginBottom: 12
              }}>
                <View style={{ flex: 1, marginRight: 12 }}>
                  <Text style={{
                    fontFamily: 'Inter-Bold',
                    fontSize: 18, // Aumentado
                    color: colors.text,
                    marginBottom: 2,
                    lineHeight: 22
                  }}>{despesa.titulo}</Text>
                  <Text style={{
                    fontFamily: 'Inter-Bold', // Ajustado para Bold
                    fontSize: 20, // Aumentado
                    color: colors.primary,
                    letterSpacing: -0.5
                  }}>{formatarValor(despesa.valor)}</Text>
                </View>
                {racha.status === 'ativo' && (
                  <View style={{
                    flexDirection: 'row',
                    gap: 8
                  }}>
                    <TouchableOpacity
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        backgroundColor: colors.primary + '15',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      onPress={() => {
                        abrirEdicaoDespesa(despesa);
                      }}
                      activeOpacity={0.7}
                    >
                      <Edit3 size={14} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        backgroundColor: colors.error + '15',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      onPress={() => {
                        confirmarExclusaoDespesa(despesa.id, despesa.titulo);
                      }}
                      activeOpacity={0.7}
                    >
                      <Trash2 size={14} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Informações consolidadas em linha (Chips) */}
              <View style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 12
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: colors.background, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: colors.border }}>
                  <User size={12} color={colors.textSecondary} />
                  <Text style={{
                    fontFamily: 'Inter-Medium',
                    fontSize: 11,
                    color: colors.textSecondary
                  }}>Pago por <Text style={{color: colors.text}}>{despesa.pagoPorNome}</Text></Text>
                </View>

                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: colors.background, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: colors.border }}>
                  <Users size={12} color={colors.textSecondary} />
                  <Text style={{
                    fontFamily: 'Inter-Medium',
                    fontSize: 11,
                    color: colors.textSecondary
                  }}>
                    {despesa.modoDivisao === 'personalizado' ? 'Personalizado' : 'Igual'}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: colors.background, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: colors.border }}>
                  <Calendar size={12} color={colors.textSecondary} />
                  <Text style={{
                    fontFamily: 'Inter-Medium',
                    fontSize: 11,
                    color: colors.textSecondary
                  }}>{formatarData(despesa.data)}</Text>
                </View>
              </View>

              {/* Mostrar detalhes da divisão de forma mais sutil */}
              <View style={[styles.divisaoDetails, { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: colors.border }]}>
                <Text style={[styles.divisaoTitle, { color: colors.textSecondary, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }]}>
                  Como foi dividido
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {despesa.divisoes.map((divisao: any) => {
                    const participante = racha.participantes.find(p => p.id === divisao.usuarioId);
                    // Só mostrar quem realmente deve algo nesta despesa para limpar a visualização
                    if (divisao.valorDevido <= 0) return null;
                    return (
                      <View key={divisao.usuarioId} style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        backgroundColor: colors.background,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: colors.border
                      }}>
                        <Image source={{ uri: participante?.foto }} style={{ width: 16, height: 16, borderRadius: 8 }} />
                        <Text style={{ fontFamily: 'Inter-Medium', fontSize: 11, color: colors.text }}>
                          {participante?.nome}
                        </Text>
                        <Text style={{ fontFamily: 'Inter-Bold', fontSize: 11, color: colors.textSecondary }}>
                          {formatarValor(divisao.valorDevido)}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </View>

              {despesa.fotoURL && (
                <Image source={{ uri: despesa.fotoURL }} style={styles.despesaFoto} />
              )}
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );

  const renderParticipantes = () => (
    <ScrollView style={styles.tabContent}>
      {renderGruposDeBolso()}
      {renderParticipantesAgrupados()}
      {racha.status === 'ativo' && (
        <>
          <TouchableOpacity
            style={styles.inviteButton}
            onPress={() => setShowAddParticipanteModal(true)}
            activeOpacity={0.7}
          >
            <UserPlus size={20} color={colors.primary} />
            <Text style={styles.inviteButtonText}>Adicionar participante</Text>
          </TouchableOpacity>
        </>
      )}
    </ScrollView>
  );

  // Função para calcular gastos consolidados por participante/bolso
  const calcularConsolidado = () => {
    const gastoPorEntidade: { [id: string]: { nome: string; gasto: number; isBolso: boolean } } = {};

    // Mapear grupos de bolso
    const bolsos = racha.gruposDeBolso || [];
    bolsos.forEach((b: GrupoDeBolso) => {
      gastoPorEntidade[b.id] = { nome: b.nome, gasto: 0, isBolso: true };
    });

    // Mapear participantes sem bolso
    racha.participantes.forEach((p: any) => {
      if (!p.grupoDeBolsoId) {
        gastoPorEntidade[p.id] = { nome: p.nome, gasto: 0, isBolso: false };
      }
    });

    // Somar despesas por divisão
    racha.despesas.forEach((d: any) => {
      d.divisoes.forEach((div: any) => {
        const participante = racha.participantes.find((p: any) => p.id === div.usuarioId);
        const entidadeId = participante?.grupoDeBolsoId || div.usuarioId;
        if (gastoPorEntidade[entidadeId]) {
          gastoPorEntidade[entidadeId].gasto += div.valorDevido;
        }
      });
    });

    return {
      gastos: Object.entries(gastoPorEntidade)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.gasto - a.gasto),
      total: racha.despesas.reduce((sum: number, d: any) => sum + d.valor, 0),
      numParticipantes: racha.participantes.length,
    };
  };

  const renderConsolidado = () => {
    const consolidado = calcularConsolidado();
    const custoMedio = consolidado.numParticipantes > 0
      ? consolidado.total / consolidado.numParticipantes
      : 0;

    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
        <View style={{ padding: 16, gap: 16 }}>
          {/* Header */}
          <View style={{
            backgroundColor: colors.surface,
            padding: 16,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: colors.border
          }}>
            <Text style={{
              fontFamily: 'Inter-Bold',
              fontSize: 18,
              color: colors.text,
              marginBottom: 8
            }}>
              Gastos Totais por Pessoa
            </Text>
            <Text style={{
              fontFamily: 'Inter-Regular',
              fontSize: 14,
              color: colors.textSecondary
            }}>
              Total consumido por cada participante (ou grupo de bolso)
            </Text>
          </View>

          {Platform.OS === 'web' && !isPremium() ? (
            <View style={{ width: '100%', height: 90, maxHeight: 90, overflow: 'hidden' as any }}>
              <AdSenseBanner
                adSlot="2112588249"
                adKey={`racha-${String(id)}-consolidado`}
                format="horizontal"
                fullWidthResponsive={false}
                style={{ height: 90, maxHeight: 90 }}
              />
            </View>
          ) : null}

          {/* Lista de gastos */}
          {consolidado.gastos.map((item, index) => (
            <View
              key={item.id}
              style={{
                backgroundColor: colors.surface,
                padding: 16,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: colors.border,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
                {item.isBolso ? (
                  <View style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: colors.primary + '20',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Users size={20} color={colors.primary} />
                  </View>
                ) : (
                  <View style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: colors.textSecondary + '20',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <User size={20} color={colors.textSecondary} />
                  </View>
                )}
                <View style={{ flex: 1 }}>
                  <Text style={{
                    fontFamily: 'Inter-SemiBold',
                    fontSize: 15,
                    color: colors.text
                  }}>
                    {item.nome}
                  </Text>
                  {item.isBolso && (
                    <Text style={{
                      fontFamily: 'Inter-Regular',
                      fontSize: 12,
                      color: colors.textSecondary
                    }}>
                      Vínculo de Bolso
                    </Text>
                  )}
                </View>
              </View>
              <Text style={{
                fontFamily: 'Inter-Bold',
                fontSize: 16,
                color: colors.primary
              }}>
                {formatarValor(item.gasto)}
              </Text>
            </View>
          ))}

          {/* Disclaimer com custo médio */}
          <View style={{
            backgroundColor: colors.primary + '10',
            padding: 16,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: colors.primary + '30'
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ fontSize: 20, marginRight: 8 }}>💡</Text>
              <Text style={{
                fontFamily: 'Inter-Bold',
                fontSize: 16,
                color: colors.text,
                flex: 1
              }}>
                O rolê custou {formatarValor(custoMedio)} por pessoa
              </Text>
            </View>
            <Text style={{
              fontFamily: 'Inter-Regular',
              fontSize: 13,
              color: colors.textSecondary,
              lineHeight: 18
            }}>
              Este valor é uma média aritmética simples. Pode variar caso existam despesas que não foram divididas entre todos os participantes.
            </Text>
          </View>

          {/* Total geral */}
          <View style={{
            backgroundColor: colors.surface,
            padding: 16,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: colors.border,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Text style={{
              fontFamily: 'Inter-SemiBold',
              fontSize: 16,
              color: colors.text
            }}>
              Total do Racha
            </Text>
            <Text style={{
              fontFamily: 'Inter-Bold',
              fontSize: 18,
              color: colors.success
            }}>
              {formatarValor(consolidado.total)}
            </Text>
          </View>
        </View>
      </ScrollView>
    );
  };


  // Função para salvar chave PIX
  const salvarChavePix = async () => {
    try {
      // Atualizar participante no racha
      const rachaAtualizado = {
        ...racha,
        participantes: racha.participantes.map(p =>
          p.id === editingParticipante.id
            ? { ...p, chavePix: editPixChave }
            : p
        )
      };

      // Salvar no contexto de rachas
      await updateRacha(racha.id, {
        participantes: rachaAtualizado.participantes
      });

      // Atualizar também em todos os grupos onde o participante está
      await updateParticipanteInAllGrupos(editingParticipante.id, { chavePix: editPixChave });

      setShowEditPixModal(false);
      setEditingParticipante(null);
      setEditPixChave(null);

      showAlert('Sucesso', 'Chave PIX atualizada com sucesso!', undefined, false);
    } catch {
      showAlert('Erro', 'Falha ao salvar chave PIX', undefined, false);
    }
  };

  // Funções para vínculos de bolso
  const adicionarGrupoDeBolso = async () => {
    if (!novoGrupoDeBolsoNome.trim()) {
      Alert.alert('Erro', 'Digite o nome do vínculo de bolso');
      return;
    }

    try {
      const novoGrupo: GrupoDeBolso = {
        id: Date.now().toString(),
        nome: novoGrupoDeBolsoNome.trim(),
      };

      const gruposAtualizados = [...(racha.gruposDeBolso || []), novoGrupo];

      await updateRacha(racha.id, {
        gruposDeBolso: gruposAtualizados,
      });

      setNovoGrupoDeBolsoNome('');
      setShowGrupoDeBolsoModal(false);
      Alert.alert('Sucesso', 'Vínculo de bolso criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar vínculo de bolso:', error);
      Alert.alert('Erro', 'Falha ao criar vínculo de bolso');
    }
  };

  const editarGrupoDeBolso = async () => {
    if (!grupoEditando || !novoGrupoDeBolsoNome.trim()) return;
    const gruposAtualizados = (racha.gruposDeBolso || []).map(g =>
      g.id === grupoEditando.id ? { ...g, nome: novoGrupoDeBolsoNome.trim() } : g
    );
    await updateRacha(racha.id, { gruposDeBolso: gruposAtualizados });
    setGrupoEditando(null);
    setNovoGrupoDeBolsoNome('');
    setShowGrupoDeBolsoModal(false);
  };

  const removerGrupoDeBolso = async (grupoId: string) => {
    // Remover grupo e desatribuir dos participantes
    const gruposAtualizados = (racha.gruposDeBolso || []).filter(g => g.id !== grupoId);
    const participantesAtualizados = racha.participantes.map(p =>
      p.grupoDeBolsoId === grupoId ? { ...p, grupoDeBolsoId: null } : p
    );
    await updateRacha(racha.id, {
      gruposDeBolso: gruposAtualizados,
      participantes: participantesAtualizados,
    });
  };

  const atribuirParticipanteAoGrupo = async (participanteId: string, grupoId: string | null) => {
    try {
      const participantesAtualizados = racha.participantes.map(p =>
        p.id === participanteId ? { ...p, grupoDeBolsoId: grupoId } : p
      );

      await updateRacha(racha.id, { participantes: participantesAtualizados });

      const participanteNome = racha.participantes.find(p => p.id === participanteId)?.nome;
      const grupoNome = grupoId ? (racha.gruposDeBolso || []).find(g => g.id === grupoId)?.nome : 'Nenhum grupo';

      Alert.alert('Sucesso', `${participanteNome} foi ${grupoId ? 'atribuído ao grupo' : 'removido do grupo'} ${grupoNome || ''}`);
    } catch (error) {
      console.error('Erro ao atribuir participante ao grupo:', error);
      Alert.alert('Erro', 'Falha ao atribuir participante ao grupo');
    }
  };

  const confirmarRemocaoParticipante = (participanteId: string, participanteNome: string) => {
    showAlert(
      'Remover Participante',
      `Tem certeza que deseja remover ${participanteNome} do racha?\n\nEsta ação irá:\n• Remover o participante de todas as despesas\n• Recalcular as divisões das despesas\n• Esta ação não pode ser desfeita`,
      async () => {
        try {
          await removeParticipanteFromRacha(racha.id, participanteId);
          showAlert('Sucesso', `${participanteNome} foi removido do racha!`, undefined, false);
        } catch (error) {
          console.error('Erro ao remover participante:', error);
          showAlert('Erro', 'Falha ao remover participante. Tente novamente.', undefined, false);
        }
      }
    );
  };

  // UI de gerenciamento de vínculos de bolso
  const renderGruposDeBolso = () => (
    <View style={{ marginBottom: 24 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 8 }}>
        <Text style={{ fontSize: 16, fontFamily: 'Inter-SemiBold', color: colors.text }}>Vínculos de Bolso</Text>
        <TouchableOpacity
          onPress={() => setShowInfoGrupoDeBolsoModal(true)}
          style={{ padding: 2 }}
          activeOpacity={0.7}
        >
          <Info size={16} color={colors.primary} />
        </TouchableOpacity>
      </View>
      {(racha.gruposDeBolso || []).length === 0 && (
        <Text style={{ color: colors.textSecondary, marginBottom: 8 }}>Nenhum grupo criado ainda.</Text>
      )}
      {(racha.gruposDeBolso || []).map(grupo => (
        <View key={grupo.id} style={{ backgroundColor: colors.surface, borderRadius: 12, padding: 16, borderWidth: 1, borderColor: colors.border, flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
          <Users size={20} color={colors.primary} style={{ marginRight: 8 }} />
          <Text style={{ flex: 1, fontSize: 15, fontFamily: 'Inter-SemiBold', color: colors.text }}>{grupo.nome}</Text>
          <TouchableOpacity onPress={() => {
            if (!isPremium()) {
              if (!isPremium()) {
                setShowPremiumModal(true);
                return;
              }
            }
            setGrupoEditando(grupo);
            setNovoGrupoDeBolsoNome(grupo.nome);
            setShowGrupoDeBolsoModal(true);
          }} style={{ marginRight: 8 }}>
            <Text style={{ color: colors.primary, fontFamily: 'Inter-Medium' }}>Editar</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => removerGrupoDeBolso(grupo.id)}>
            <Text style={{ color: colors.error, fontFamily: 'Inter-Medium' }}>Remover</Text>
          </TouchableOpacity>
        </View>
      ))}
      <TouchableOpacity
        style={{ marginTop: 10, backgroundColor: colors.primary, borderRadius: 12, padding: 12, alignItems: 'center' }}
        onPress={() => {
          if (!isPremium()) {
            if (!isPremium()) {
              setShowPremiumModal(true);
              return;
            }
          }
          setGrupoEditando(null);
          setNovoGrupoDeBolsoNome('');
          setShowGrupoDeBolsoModal(true);
        }}
      >
        <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 15 }}>+ Novo Vínculo de Bolso</Text>
      </TouchableOpacity>
    </View>
  );

  // Modal de criação/edição de vínculo de bolso
  const renderGrupoDeBolsoModal = () => (
    <Modal visible={showGrupoDeBolsoModal} transparent animationType="slide">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ backgroundColor: colors.surface, borderRadius: 16, padding: 0, width: '90%', maxWidth: 400 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: colors.border }}>
            <Text style={{ fontSize: 18, fontFamily: 'Inter-SemiBold', color: colors.text }}>
              {grupoEditando ? 'Editar Vínculo de Bolso' : 'Novo Vínculo de Bolso'}
            </Text>
            <TouchableOpacity onPress={() => {
              setShowGrupoDeBolsoModal(false);
              setGrupoEditando(null);
              setNovoGrupoDeBolsoNome('');
            }}>
              <X size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
          <View style={{ paddingHorizontal: 24, paddingTop: 24, paddingBottom: 16 }}>
            <View style={{ marginBottom: 24 }}>
              <Text style={{ fontSize: 14, fontFamily: 'Inter-SemiBold', color: colors.text, marginBottom: 8 }}>Nome do grupo</Text>
              <TextInput
                style={{ backgroundColor: colors.surface, borderRadius: 12, paddingHorizontal: 16, height: 48, fontSize: 16, fontFamily: 'Inter-Regular', color: colors.text, borderWidth: 1, borderColor: colors.border }}
                placeholder="Ex: Casal, Família, Amigos..."
                placeholderTextColor={colors.textSecondary}
                value={novoGrupoDeBolsoNome}
                onChangeText={setNovoGrupoDeBolsoNome}
                maxLength={24}
              />
            </View>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity
                style={{ flex: 1, backgroundColor: colors.border, borderRadius: 12, paddingVertical: 12, alignItems: 'center' }}
                onPress={() => {
                  setShowGrupoDeBolsoModal(false);
                  setGrupoEditando(null);
                  setNovoGrupoDeBolsoNome('');
                }}
              >
                <Text style={{ fontSize: 14, fontFamily: 'Inter-Medium', color: colors.text }}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, backgroundColor: colors.primary, borderRadius: 12, paddingVertical: 12, alignItems: 'center' }}
                onPress={grupoEditando ? editarGrupoDeBolso : adicionarGrupoDeBolso}
              >
                <Text style={{ fontSize: 14, fontFamily: 'Inter-SemiBold', color: '#FFF' }}>{grupoEditando ? 'Salvar' : 'Criar'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Modal de informação sobre Vínculo de Bolso
  const renderInfoGrupoDeBolsoModal = () => (
    <Modal visible={showInfoGrupoDeBolsoModal} transparent animationType="fade">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ backgroundColor: colors.surface, borderRadius: 16, padding: 0, width: '90%', maxWidth: 400 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: colors.border }}>
            <Text style={{ fontSize: 18, fontFamily: 'Inter-SemiBold', color: colors.text }}>
              Vínculo de Bolso
            </Text>
            <TouchableOpacity onPress={() => setShowInfoGrupoDeBolsoModal(false)}>
              <X size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
          <View style={{ paddingHorizontal: 24, paddingVertical: 24 }}>
            <Text style={{ fontSize: 14, fontFamily: 'Inter-Regular', color: colors.text, lineHeight: 20 }}>
              O Vínculo de Bolso serve para agrupar pessoas que dividem as mesmas finanças, como um casal ou família. As despesas são divididas por pessoa, mas na hora de acertar as contas, o saldo do grupo é consolidado, simplificando os pagamentos.
            </Text>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Renderizar participantes agrupados por vínculo de bolso
  const renderParticipantesAgrupados = () => {
    const grupos = (racha.gruposDeBolso || []);
    const participantesSemGrupo = racha.participantes.filter(p => !p.grupoDeBolsoId);
    return (
      <View>
        {grupos.map(grupo => {
          const count = racha.participantes.filter(p => p.grupoDeBolsoId === grupo.id).length;
          return (
            <View key={grupo.id} style={{ marginBottom: 18, backgroundColor: colors.surface, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: colors.border }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Users size={18} color={colors.primary} style={{ marginRight: 6 }} />
                <Text style={{ fontWeight: 'bold', fontSize: 15, color: colors.primary, fontFamily: 'Inter-SemiBold' }}>{grupo.nome} <Text style={{ color: colors.textSecondary, fontFamily: 'Inter-Regular' }}>({count})</Text></Text>
              </View>
              {count === 0 && (
                <Text style={{ color: colors.textSecondary, marginBottom: 6 }}>Nenhum participante neste grupo.</Text>
              )}
              {racha.participantes.filter(p => p.grupoDeBolsoId === grupo.id).map(participante => (
                <View key={participante.id} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                  <Image source={{ uri: participante.foto }} style={{ width: 32, height: 32, borderRadius: 16, marginRight: 8 }} />
                  <Text style={{ flex: 1, fontFamily: 'Inter-Regular', color: colors.text }}>{participante.nome}</Text>
                  <TouchableOpacity
                    style={{ marginRight: 8 }}
                    onPress={() => atribuirParticipanteAoGrupo(participante.id, null)}
                  >
                    <Text style={{ color: colors.error, fontFamily: 'Inter-Medium' }}>Remover do grupo</Text>
                  </TouchableOpacity>
                  {racha.status === 'ativo' && participante.id !== racha.criadorId && (
                    <TouchableOpacity
                      style={{ padding: 4 }}
                      onPress={() => confirmarRemocaoParticipante(participante.id, participante.nome)}
                    >
                      <Trash2 size={16} color={colors.error} />
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            </View>
          );
        })}
        {participantesSemGrupo.length > 0 && (
          <View style={{ marginBottom: 18, backgroundColor: colors.surface, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: colors.border }}>
            <Text style={{ fontWeight: 'bold', fontSize: 15, color: colors.text, fontFamily: 'Inter-SemiBold', marginBottom: 6 }}>Sem vínculo de bolso</Text>
            {participantesSemGrupo.map(participante => (
              <View key={participante.id} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Image source={{ uri: participante.foto }} style={{ width: 32, height: 32, borderRadius: 16, marginRight: 8 }} />
                <Text style={{ flex: 1, fontFamily: 'Inter-Regular', color: colors.text }}>{participante.nome}</Text>
                {(racha.gruposDeBolso || []).length > 0 && (
                  <TouchableOpacity
                    onPress={() => {
                      if (!isPremium()) {
                        if (!isPremium()) {
                          setShowPremiumModal(true);
                          return;
                        }
                      }
                      setGrupoDeBolsoSelecionado(participante.id);
                    }}
                    style={{ marginRight: 8 }}
                  >
                    <Text style={{ color: colors.primary, fontFamily: 'Inter-Medium' }}>Atribuir a grupo</Text>
                  </TouchableOpacity>
                )}
                {racha.status === 'ativo' && participante.id !== racha.criadorId && (
                  <TouchableOpacity
                    style={{ padding: 4 }}
                    onPress={() => confirmarRemocaoParticipante(participante.id, participante.nome)}
                  >
                    <Trash2 size={16} color={colors.error} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
          </View>
        )}
        {/* Modal para atribuir participante a grupo */}
        <Modal visible={!!grupoDeBolsoSelecionado} transparent animationType="fade">
          <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: colors.surface, borderRadius: 16, padding: 24, width: '80%' }}>
              <Text style={{ fontSize: 16, fontFamily: 'Inter-SemiBold', color: colors.text, marginBottom: 12 }}>Atribuir a vínculo de bolso</Text>
              {(racha.gruposDeBolso || []).map(grupo => (
                <TouchableOpacity
                  key={grupo.id}
                  style={{ padding: 10, borderBottomWidth: 1, borderColor: colors.border }}
                  onPress={async () => {
                    await atribuirParticipanteAoGrupo(grupoDeBolsoSelecionado!, grupo.id);
                    setGrupoDeBolsoSelecionado(null);
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Users size={16} color={colors.primary} style={{ marginRight: 6 }} />
                    <Text style={{ fontFamily: 'Inter-Regular', color: colors.text }}>{grupo.nome}</Text>
                  </View>
                </TouchableOpacity>
              ))}
              <TouchableOpacity onPress={() => setGrupoDeBolsoSelecionado(null)} style={{ marginTop: 16 }}>
                <Text style={{ color: colors.error, fontFamily: 'Inter-Medium' }}>Cancelar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </View>
    );
  };

  return (
    <>
      <SafeAreaView style={styles.container}>
        <Breadcrumb
          items={[
            { label: 'Meus Rachas', onPress: () => router.back() },
            { label: racha.nome }
          ]}
        />

        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <ArrowLeft size={24} color={colors.text} />
          </TouchableOpacity>

          <View style={styles.headerContent}>
            <Text style={styles.headerEmoji}>{racha.emoji}</Text>
            <View style={styles.headerText}>
              <Text style={styles.headerTitle}>{racha.nome}</Text>
              <View style={styles.headerMetadata}>
                <View style={styles.participantesContainer}>
                  <Users size={14} color={colors.textSecondary} />
                  <Text style={styles.headerSubtitle}>
                    {racha.participantes.length} participantes
                  </Text>
                </View>
              </View>
              <Text style={styles.headerTotal}>
                Total: {formatarValor(getTotalDespesas(racha.id))}
              </Text>
            </View>
          </View>

          {racha.criadorId === user?.id && (
            <View style={styles.statusActions}>
              {racha.status === 'ativo' ? (
                <TouchableOpacity
                  style={styles.finalizarButtonCompact}
                  onPress={() => confirmarFinalizacao()}
                  activeOpacity={0.8}
                >
                  <Text style={styles.finalizarButtonCompactText}>Finalizar</Text>
                </TouchableOpacity>
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={styles.statusButton}>
                    <Text style={[styles.statusBadgeText, { color: colors.textSecondary }]}>
                      Finalizado
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={[styles.statusButton, { backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }]}
                    onPress={() => {
                      if (user?.plano === 'vitalicio' || user?.plano === 'anual') {
                        setAlertConfig({
                          title: 'Reabrir Racha?',
                          message: 'Ao reabrir, o racha voltará para a aba "Ativos".',
                          showCancel: true,
                          onConfirm: async () => {
                            await reativarRacha(racha.id);
                          }
                        });
                        setShowNativeAlert(true);
                      } else {
                        setShowPremiumModal(true);
                      }
                    }}
                  >
                    {(user?.plano === 'vitalicio' || user?.plano === 'anual') ? (
                      <RotateCcw size={16} color={colors.primary} />
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <RotateCcw size={16} color={colors.textSecondary} />
                        <View style={{ backgroundColor: colors.primary, borderRadius: 6, padding: 2 }}>
                          <Lock size={8} color="#fff" />
                        </View>
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        </View>

        <View style={styles.tabs}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'despesas' && styles.activeTab]}
            onPress={() => setActiveTab('despesas')}
          >
            <Text style={[styles.tabText, activeTab === 'despesas' && styles.activeTabText]}>
              Despesas
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tab, activeTab === 'balanco' && styles.activeTab]}
            onPress={() => setActiveTab('balanco')}
          >
            <Text style={[styles.tabText, activeTab === 'balanco' && styles.activeTabText]}>
              Balanço
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tab, activeTab === 'consolidado' && styles.activeTab]}
            onPress={() => setActiveTab('consolidado')}
          >
            <Text style={[styles.tabText, activeTab === 'consolidado' && styles.activeTabText]}>
              Consolidado
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tab, activeTab === 'participantes' && styles.activeTab]}
            onPress={() => setActiveTab('participantes')}
          >
            <Text style={[styles.tabText, activeTab === 'participantes' && styles.activeTabText]}>
              Participantes
            </Text>
          </TouchableOpacity>
        </View>

        {activeTab === 'despesas' && renderDespesas()}
        {activeTab === 'balanco' && (() => {
          const transacoesCalculadas = calcularBalanco();

          return (
            <Balanco
              transacoes={transacoesCalculadas}
              racha={racha}
              participantes={racha.participantes}
              formatarValor={formatarValor}
              copiarChavePix={copiarChavePix}
              colors={colors}
              isPremium={isPremium}
            />
          );
        })()}
        {activeTab === 'consolidado' && renderConsolidado()}
        {activeTab === 'participantes' && renderParticipantes()}

        {activeTab === 'despesas' && racha.status === 'ativo' && (
          <TouchableOpacity
            style={styles.fab}
            onPress={() => setShowAddExpenseModal(true)}
            activeOpacity={0.8}
          >
            <Plus size={24} color="#FFFFFF" />
          </TouchableOpacity>
        )}

        {/* Modal Adicionar Despesa */}
        <Modal
          visible={showAddExpenseModal}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowAddExpenseModal(false)}
        >
          <SafeAreaView style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowAddExpenseModal(false)}>
                <Text style={styles.modalCancelButton}>Cancelar</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Nova Despesa</Text>
              <TouchableOpacity onPress={adicionarDespesa}>
                <Text style={styles.modalCreateButton}>Adicionar</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Título da despesa</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Ex: Almoço no restaurante"
                  placeholderTextColor={colors.textSecondary}
                  value={novaDespesaTitulo}
                  onChangeText={setNovaDespesaTitulo}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Valor</Text>
                <MoneyInput
                  style={styles.input}
                  placeholder="0,00"
                  value={novaDespesaValor}
                  onChangeValue={setNovaDespesaValor}
                />
              </View>

              <CategoriaSelector
                categoriaSelecionada={categoriaSelecionada}
                onCategoriaChange={setCategoriaSelecionada}
                permitirCriar={true}
              />

              {/* Seletor de Data */}
              {/* Seletor de Data */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Data da despesa</Text>

                {Platform.OS === 'web' ? (
                  <View style={[styles.input, { padding: 0, justifyContent: 'center' }]}>
                    <input
                      type="date"
                      value={novaDespesaData.toISOString().split('T')[0]}
                      onChange={(e) => {
                        if (e.target.value) {
                          // Manter o horário atual ou meio-dia para evitar problemas de fuso
                          const date = new Date(e.target.value + 'T12:00:00');
                          setNovaDespesaData(date);
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: colors.text,
                        fontFamily: 'Inter-Medium',
                        fontSize: '16px',
                        padding: '0 16px',
                      }}
                    />
                  </View>
                ) : (
                  <TouchableOpacity
                    style={[styles.input, { justifyContent: 'center' }]}
                    onPress={() => {
                      // Para mobile, usar Alert com seleção simples
                      // TODO: Futuramente implementar @react-native-community/datetimepicker
                      const hoje = new Date();
                      const opcoes = [
                        { label: 'Hoje', date: hoje },
                        { label: 'Ontem', date: new Date(hoje.getTime() - 86400000) },
                        { label: 'Anteontem', date: new Date(hoje.getTime() - 2 * 86400000) },
                      ];
                      Alert.alert(
                        'Selecionar Data',
                        'Escolha uma data',
                        (opcoes.map(op => ({
                          text: `${op.label} (${op.date.getDate()}/${op.date.getMonth() + 1})`,
                          onPress: () => setNovaDespesaData(op.date),
                          style: 'default' as const
                        })) as any[]).concat([{ text: 'Cancelar', style: 'cancel' as const, onPress: () => { } }])
                      );
                    }}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Calendar size={18} color={colors.textSecondary} />
                      <Text style={{ color: colors.text, fontFamily: 'Inter-Medium', fontSize: 16 }}>
                        {formatarData(novaDespesaData)}
                      </Text>
                    </View>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Pago por</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexDirection: 'row' }}>
                  <View style={{ flexDirection: 'row', gap: 12, paddingBottom: 8 }}>
                    {racha.participantes.map((participante) => (
                      <TouchableOpacity
                        key={participante.id}
                        style={[
                          {
                            flexDirection: 'row',
                            alignItems: 'center',
                            backgroundColor: colors.surface,
                            borderRadius: 20,
                            paddingVertical: 8,
                            paddingHorizontal: 12,
                            borderWidth: 1,
                            borderColor: novaDespesaPagoPor === participante.id ? colors.primary : colors.border,
                            gap: 8,
                          },
                          novaDespesaPagoPor === participante.id && {
                            backgroundColor: colors.primary + '15',
                          }
                        ]}
                        onPress={() => setNovaDespesaPagoPor(participante.id)}
                      >
                        <Image source={{ uri: participante.foto }} style={{ width: 24, height: 24, borderRadius: 12 }} />
                        <Text style={[
                          { fontFamily: 'Inter-Medium', fontSize: 14, color: colors.text },
                          novaDespesaPagoPor === participante.id && { color: colors.primary, fontFamily: 'Inter-Bold' }
                        ]}>
                          {participante.nome}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </ScrollView>
              </View>

              <View style={styles.inputGroup}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <View>
                    <Text style={styles.inputLabel}>Dividir entre</Text>
                    <Text style={styles.inputSubLabel}>Selecione quem participou desta despesa</Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => {
                      if (participantesSelecionados.length === racha.participantes.length) {
                        setParticipantesSelecionados([]);
                      } else {
                        setParticipantesSelecionados(racha.participantes.map(p => p.id));
                      }
                    }}
                  >
                    <Text style={{ color: colors.primary, fontFamily: 'Inter-Medium', fontSize: 13 }}>
                      {participantesSelecionados.length === racha.participantes.length ? 'Desmarcar todos' : 'Marcar todos'}
                    </Text>
                  </TouchableOpacity>
                </View>
                
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {racha.participantes.map((participante) => {
                    const isSelected = participantesSelecionados.includes(participante.id);
                    return (
                      <TouchableOpacity
                        key={participante.id}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: isSelected ? colors.primary : colors.surface,
                          borderRadius: 20,
                          paddingVertical: 8,
                          paddingHorizontal: 12,
                          borderWidth: 1,
                          borderColor: isSelected ? colors.primary : colors.border,
                          gap: 6,
                        }}
                        onPress={() => {
                          if (isSelected) {
                            setParticipantesSelecionados(prev => prev.filter(id => id !== participante.id));
                          } else {
                            setParticipantesSelecionados(prev => [...prev, participante.id]);
                          }
                        }}
                      >
                        <Image source={{ uri: participante.foto }} style={{ width: 20, height: 20, borderRadius: 10 }} />
                        <Text style={{ 
                          fontFamily: isSelected ? 'Inter-SemiBold' : 'Inter-Medium', 
                          fontSize: 13, 
                          color: isSelected ? '#FFF' : colors.text 
                        }}>
                          {participante.nome}
                        </Text>
                        {isSelected && <Check size={14} color="#FFF" style={{ marginLeft: 2 }} />}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              <DivisaoDespesa
                participantes={racha.participantes.filter(p => participantesSelecionados.includes(p.id))}
                valorTotal={parseFloat(novaDespesaValor.replace(',', '.')) || 0}
                modoDivisao={modoDivisao}
                divisoesPersonalizadas={divisoesPersonalizadas}
                onModoDivisaoChange={setModoDivisao}
                onDivisoesChange={setDivisoesPersonalizadas}
              />
            </ScrollView>
          </SafeAreaView>
        </Modal>

        {/* Modal Editar Despesa */}
        <Modal
          visible={showEditExpenseModal}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowEditExpenseModal(false)}
        >
          <SafeAreaView style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowEditExpenseModal(false)}>
                <Text style={styles.modalCancelButton}>Cancelar</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Editar Despesa</Text>
              <TouchableOpacity onPress={editarDespesa}>
                <Text style={styles.modalCreateButton}>Salvar</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Título da despesa</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Ex: Almoço no restaurante"
                  placeholderTextColor={colors.textSecondary}
                  value={editDespesaTitulo}
                  onChangeText={setEditDespesaTitulo}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Valor</Text>
                <MoneyInput
                  style={styles.input}
                  placeholder="0,00"
                  value={editDespesaValor}
                  onChangeValue={setEditDespesaValor}
                />
              </View>

              <CategoriaSelector
                categoriaSelecionada={editCategoriaSelecionada}
                onCategoriaChange={setEditCategoriaSelecionada}
                permitirCriar={true}
              />

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Pago por</Text>
                <View style={styles.pagadorList}>
                  {racha.participantes.map((participante) => (
                    <TouchableOpacity
                      key={participante.id}
                      style={[
                        styles.pagadorOption,
                        editDespesaPagoPor === participante.id && styles.pagadorSelected
                      ]}
                      onPress={() => setEditDespesaPagoPor(participante.id)}
                    >
                      <Image source={{ uri: participante.foto }} style={styles.pagadorFoto} />
                      <Text style={[
                        styles.pagadorNome,
                        editDespesaPagoPor === participante.id && styles.pagadorNomeSelected
                      ]}>
                        {participante.nome}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Participantes</Text>
                <Text style={styles.inputSubLabel}>Selecione quem participou desta despesa</Text>
                <View style={styles.participantesSelection}>
                  {racha.participantes.map((participante) => (
                    <TouchableOpacity
                      key={participante.id}
                      style={[
                        styles.participanteOption,
                        editParticipantesSelecionados.includes(participante.id) && styles.participanteSelected
                      ]}
                      onPress={() => toggleEditParticipante(participante.id)}
                    >
                      <Image source={{ uri: participante.foto }} style={styles.participanteFotoSmall} />
                      <Text style={[
                        styles.participanteOptionNome,
                        editParticipantesSelecionados.includes(participante.id) && styles.participanteOptionNomeSelected
                      ]}>
                        {participante.nome}
                      </Text>
                      <View style={[
                        styles.checkbox,
                        editParticipantesSelecionados.includes(participante.id) && styles.checkboxSelected
                      ]}>
                        {editParticipantesSelecionados.includes(participante.id) && (
                          <CheckSquare size={16} color="#FFFFFF" />
                        )}
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <DivisaoDespesa
                participantes={racha.participantes.filter(p => editParticipantesSelecionados.includes(p.id))}
                valorTotal={parseFloat(editDespesaValor.replace(',', '.')) || 0}
                modoDivisao={editModoDivisao}
                divisoesPersonalizadas={editDivisoesPersonalizadas}
                onModoDivisaoChange={setEditModoDivisao}
                onDivisoesChange={setEditDivisoesPersonalizadas}
              />
            </ScrollView>
          </SafeAreaView>
        </Modal>



        {/* Modal Adicionar Participante */}
        <Modal
          visible={showAddParticipanteModal}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowAddParticipanteModal(false)}
        >
          <SafeAreaView style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowAddParticipanteModal(false)}>
                <Text style={styles.modalCancelButton}>Cancelar</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Adicionar Participante</Text>
              <TouchableOpacity onPress={adicionarNovoParticipante} disabled={!novoParticipante.nome.trim()}>
                <Text style={[styles.modalCreateButton, { opacity: !novoParticipante.nome.trim() ? 0.5 : 1 }]}>Salvar</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              {participantesDisponiveis.length > 0 && (
                <>
                  <Text style={styles.inputLabel}>Participantes disponíveis</Text>
                  <Text style={styles.inputSubLabel}>Selecione um participante já cadastrado</Text>
                  <View style={styles.participantesDisponiveis}>
                    {participantesDisponiveis.map((participante) => (
                      <View key={participante.id} style={styles.participanteDisponivel}>
                        <Image source={{ uri: participante.foto }} style={styles.participanteFotoSmall} />
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.participanteOptionNome, { color: colors.text }]}>
                            {participante.nome}
                          </Text>
                          {participante.chavePix && (
                            <Text style={styles.participantePixKey}>
                              PIX: {participante.chavePix.valor}
                            </Text>
                          )}
                        </View>
                        <TouchableOpacity
                          style={styles.addButton}
                          onPress={() => adicionarParticipanteExistente(participante)}
                        >
                          <Text style={styles.addButtonText}>Adicionar</Text>
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>

                  <View style={styles.divider} />
                  <Text style={styles.dividerText}>ou</Text>
                </>
              )}

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Criar novo participante</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Nome do participante"
                  placeholderTextColor={colors.textSecondary}
                  value={novoParticipante.nome}
                  onChangeText={(text) => setNovoParticipante(prev => ({ ...prev, nome: text }))}
                />
              </View>

              <View style={styles.inputGroup}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Text style={styles.inputLabel}>Email</Text>
                  <Text style={styles.optionalLabel}>(opcional)</Text>
                </View>
                <TextInput
                  style={styles.input}
                  placeholder="email@exemplo.com"
                  placeholderTextColor={colors.textSecondary}
                  value={novoParticipante.email}
                  onChangeText={(text) => setNovoParticipante(prev => ({ ...prev, email: text }))}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              <ChavePixSelector
                value={novoParticipante.chavePixPadrao ? {
                  tipo: 'aleatoria' as const,
                  valor: novoParticipante.chavePixPadrao
                } : undefined}
                onChange={(chavePix) => setNovoParticipante(prev => ({
                  ...prev,
                  chavePixPadrao: chavePix?.valor || ''
                }))}
              />

              <TouchableOpacity
                style={styles.addParticipanteButton}
                onPress={adicionarNovoParticipante}
                disabled={!novoParticipante.nome.trim()}
              >
                <Text style={styles.addParticipanteButtonText}>
                  Adicionar Participante
                </Text>
              </TouchableOpacity>
            </ScrollView>
          </SafeAreaView>
        </Modal>



        {/* Modal Editar PIX */}
        <Modal
          visible={showEditPixModal}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowEditPixModal(false)}
        >
          <SafeAreaView style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowEditPixModal(false)}>
                <Text style={styles.modalCancelButton}>Cancelar</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Editar Chave PIX</Text>
              <TouchableOpacity onPress={salvarChavePix}>
                <Text style={styles.modalCreateButton}>Salvar</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.modalContent}>
              {editingParticipante && (
                <>
                  <View style={styles.participanteCard}>
                    <Image source={{ uri: editingParticipante.foto }} style={styles.participanteFoto} />
                    <Text style={styles.participanteNome}>{editingParticipante.nome}</Text>
                  </View>

                  <ChavePixSelector
                    value={editPixChave}
                    onChange={setEditPixChave}
                  />
                </>
              )}
            </View>
          </SafeAreaView>
        </Modal>

        {/* Modal Filtros */}
        <Modal
          visible={showFiltrosModal}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowFiltrosModal(false)}
        >
          <SafeAreaView style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowFiltrosModal(false)}>
                <Text style={styles.modalCancelButton}>Fechar</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Filtrar por Categoria</Text>
              <View />
            </View>

            <ScrollView style={styles.modalContent}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Selecione uma categoria</Text>
                <View style={styles.categoriasGrid}>
                  {categoriasPadrao.map((categoria) => (
                    <TouchableOpacity
                      key={categoria.id}
                      style={[
                        styles.categoriaOption,
                        categoriaFiltro === categoria.id && styles.categoriaOptionSelected
                      ]}
                      onPress={() => {
                        setCategoriaFiltro(categoria.id);
                        setShowFiltrosModal(false);
                      }}
                    >
                      <Text style={styles.categoriaOptionEmoji}>{categoria.emoji}</Text>
                      <Text style={[
                        styles.categoriaOptionNome,
                        { color: categoria.cor },
                        categoriaFiltro === categoria.id && styles.categoriaOptionNomeSelected
                      ]}>
                        {categoria.nome}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>
          </SafeAreaView>
        </Modal>

        {/* Modal Vínculo de Bolso */}
        {renderGrupoDeBolsoModal()}
        {renderInfoGrupoDeBolsoModal()}
      </SafeAreaView>

      {/* Modal Premium Feature */}
      <PremiumFeatureModal
        visible={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureName="Vínculo de Bolso"
        description="O Vínculo de Bolso é exclusivo para usuários Premium. Agrupe casais e famílias para simplificar os acertos."
      />

      {/* Alert Nativo - Modal com máxima prioridade */}
      <Modal
        visible={showNativeAlert}
        transparent={true}
        statusBarTranslucent={true}
        animationType="fade"
        onRequestClose={() => setShowNativeAlert(false)}
      >
        <View style={[styles.alertOverlay, { zIndex: 99999, elevation: 99999 }]}>
          <View style={styles.alertContainer}>
            <Text style={styles.alertTitle}>{alertConfig.title}</Text>
            <Text style={styles.alertMessage}>{alertConfig.message}</Text>
            <View style={styles.alertButtons}>
              {alertConfig.showCancel && (
                <TouchableOpacity
                  style={[styles.alertButton, styles.alertButtonCancel]}
                  onPress={() => setShowNativeAlert(false)}
                >
                  <Text style={styles.alertButtonCancelText}>Cancelar</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.alertButton, styles.alertButtonConfirm]}
                onPress={async () => {
                  setShowNativeAlert(false);
                  if (alertConfig.onConfirm) {
                    await alertConfig.onConfirm();
                  }
                }}
              >
                <Text style={styles.alertButtonConfirmText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Banner Intersticial para usuários não-premium */}
      {showBannerIntersticial && (
        <BannerIntersticial onClose={handleCloseBanner} />
      )}
    </>
  );
}
